import { useCallback, useEffect, useRef, useState } from "react";
import type { MutableRefObject } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { configHarta, sursaZona, type ConfigHarta, type Latura,
         type ZonaAcces } from "./dosarclient";
import { ancoraFetei, asezareaEtichetei, centrulWgs, inimaPoligonului,
         laturaDeSub, laturaLogica,
         laturaLangaPoligon, segmentLangaPoligon,
         puncteleRetragerilor, sectiuniContur,
         zonaEdificabila, zonaPesteLimita } from "./edificabil";
import { ariaBucatii, taiePeLinie, type Linie } from "./zoneRegim";
import { numar, t as tr, useT, useTn } from "../i18n";

// Harta lotului.
//
// **De ce e agnostică la furnizor.** Componenta nu știe de OpenStreetMap și nu
// știe de Google: primește un șablon de URL de dale de la server
// (`GET /api/harta`) și îl desenează. Trecerea la Google se face punând
// `MAP_TILE_URL` pe endpoint-ul Google Map Tiles API (care servește dale
// raster, exact forma asta) și repornind containerul — fără nicio linie
// schimbată aici. Motivul pentru care configurația vine de la server, nu din
// `import.meta.env`: `Dockerfile` n-are niciun `ARG` în etapa de frontend, deci
// o variabilă `VITE_*` n-ar ajunge niciodată în bundle.
//
// Leaflet se creează în `useEffect`, niciodată la nivel de modul: pe un `div`
// de dimensiune zero (jsdom, sau un părinte care încă nu s-a așezat) `L.map()`
// se poartă imprevizibil.

// Pinul e desenat, nu încărcat: pictogramele implicite ale lui Leaflet sunt
// PNG-uri rezolvate relativ la CSS, iar aplicația stă sub `/plan-generator/`.
// Un `divIcon` n-are ce să rateze și iese în culoarea machetei.
const PIN = L.divIcon({
  className: "",
  html: `<svg width="26" height="34" viewBox="0 0 26 34" aria-hidden="true">
    <path d="M13 33S2 20.5 2 12.5A11 11 0 0 1 24 12.5C24 20.5 13 33 13 33z"
          fill="#0b7f92" stroke="#fff" stroke-width="2"/>
    <circle cx="13" cy="12.5" r="4.2" fill="#fff"/></svg>`,
  iconSize: [26, 34],
  iconAnchor: [13, 33],
});

// Mânerul de rotire al terenului (modul «mută terenul»): o săgeată în cerc,
// pe un disc, ca să se vadă că e altceva decât un colț.
const ROTIRE = L.divIcon({
  className: "",
  html: `<svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
    <circle cx="14" cy="14" r="12.5" fill="#fff" stroke="#0b7f92" stroke-width="2"/>
    <path d="M19.5 10.5A7 7 0 1 0 21 14" fill="none" stroke="#0b7f92" stroke-width="2.2"
          stroke-linecap="round"/>
    <path d="M19.5 6.5v4.5H15" fill="none" stroke="#0b7f92" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

// Mânerele de editare. Tot `divIcon`, din același motiv ca pinul, dar și
// pentru unul în plus: un `L.circleMarker` NU se poate trage. Colțurile erau
// desenate cu el, și de aici venea „nu îmi dau seama dacă te lasă să tragi de
// puncte" — nu te lăsa, iar nimic din desen nu spunea asta.
const COLT = L.divIcon({
  className: "",
  html: '<div class="ob-maner ob-colt"></div>',
  iconSize: [16, 16], iconAnchor: [8, 8],
});

// Mânerul de mijloc de latură: de aici se naște un colț nou. Fără el, singurul
// fel de a îndesi conturul era să-l desenezi din nou, de la primul clic.
const MIJLOC = L.divIcon({
  className: "",
  html: '<div class="ob-maner ob-mijloc"></div>',
  iconSize: [14, 14], iconAnchor: [7, 7],
});

// NUMELE STRĂZII, pe latura care dă spre ea.
//
// Frontul stradal e citit din acte (planul topo, cadastrul, anexa
// certificatului) și e o dată a proiectului, nu o unealtă: decide retragerea
// laturii și unde poate veni accesul carosabil. Se scrie ca o cotă, în afara
// conturului, în culoarea străzii.
const scapa = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Primul capăt al unei zone în curs de marcare.
const CAPAT = L.divIcon({
  className: "",
  html: '<div class="ob-capat"></div>',
  iconSize: [14, 14], iconAnchor: [7, 7],
});

// Culorile: strada și zonele din acte, zonele marcate de om, și gura de acces
// EXISTENTĂ citită de pe topo — cea pe care motorul o preferă.
const CULOARE_STRADA = "#1f4e79";
// DRUMUL ca bucată de teren: ALBASTRU ÎNCHIS, ca strada.
//
// A fost gri până pe 16.10.2026, ca să se deosebească de stradă — strada e o
// LATURĂ a lotului, drumul o SUPRAFAȚĂ de lângă el. Cerut altfel de șeful
// Biancăi, și cu temei: pentru arhitect amândouă sunt același lucru, circulația
// pe care se intră în lot, iar două albastre apropiate nu se bat cap în cap
// fiindcă una e linie și alta e suprafață. Aceeași valoare ca a străzii, ca să
// se citească drept aceeași familie.
const CULOARE_DRUM = CULOARE_STRADA;


/** Cât de aproape se poate ajunge, dincolo de ultimul nivel cu dale.
 *
 *  22 e maximul pe care îl duce Leaflet cu dale mărite fără să se vadă mozaic;
 *  la nivelul ăsta un metru are vreo 30 px, deci o retragere de 3 m se poate
 *  apuca și măsura cu ochiul. */
const ZOOM_MAX = 22;

const CULOARE_ZONA_MANUALA = "#a97614";
const CULOARE_ZONA_EXACTA = "#0b7a3b";

// ZONELE DE REGIM (UTR Lc / UTR M1 pe același lot): două culori care nu sunt
// nici ale străzii, nici ale interdicției, nici ale zonelor de acces — o
// împărțire a terenului e altceva decât toate astea. Se alternează pe listă;
// pe mai mult de două zone a treia o ia din nou pe prima, dar cu altă etichetă.
const CULORI_ZONA_REGIM = ["#6b3fa0", "#c2571a", "#1f7a6b"];

// Sub cât nu încape un acces cu două fire plus platforma pietonală (ghid:
// 5,50 + 1,50 m). Aceeași valoare ca `importer/dosar/zone_acces.LATIME_MIN_ZONA`.
const LATIME_MIN_ZONA_M = 7;

// Cât de aproape de contur trebuie să cadă clicul ca să însemne „aici e poarta",
// în PIXELI. În metri ar fi o toleranță care se schimbă cu zoomul: la scara
// întregului cartier, 5 m sunt sub un pixel.
const LIPIRE_PX = 34;

// Dalele pe care se POATE desena. Vezi efectul de schimbare a fundalului.
const OSM_DALE = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATRIBUIRE = "© OpenStreetMap";

// Fără contur și fără adresă geocodată, harta se deschide pe România. Un
// (0, 0) ar arăta oceanul și ar părea o eroare.
const ROMANIA: L.LatLngTuple = [45.9432, 24.9668];

// Aria unui poligon lat/lon, în metri pătrați.
//
// Proiecție echidreptunghiulară în jurul primului punct: pe câteva sute de
// metri — adică pe orice lot — eroarea e sub o miime, și nu cere nici o
// bibliotecă. Serverul recalculează oricum exact, în Stereo 70; aici e doar
// cifra pe care omul o vede cât desenează.
export function arie(pts: [number, number][]): number {
  if (pts.length < 3) return 0;
  const lat0 = (pts.reduce((s, p) => s + p[0], 0) / pts.length) * Math.PI / 180;
  // Metrii pe grad de LATITUDINE depind de latitudine: 110 574 la ecuator,
  // 111 694 la pol. Aici stătea 110 540 — valoarea de la ecuator — care în
  // România citește aria cu ~0,6 % în minus, adică vreo 190 m² pe un lot de
  // 27 000. Suficient cât să treacă drept o nepotrivire cu actele.
  const mx = 111320 * Math.cos(lat0);
  const my = 111132.92 - 559.82 * Math.cos(2 * lat0) + 1.175 * Math.cos(4 * lat0);
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += (a[1] * mx) * (b[0] * my) - (b[1] * mx) * (a[0] * my);
  }
  return Math.abs(s) / 2;
}

// Lungimea unei laturi, în metri. Aceeași aproximare ca la `arie`, din același
// motiv: e cifra de pe ecran, nu cea din planșă.
/** Măririle sub care încadrarea încă are ce aduce. `incadreaza` le alege. */
export const ZOOM_CONTUR_MIN = 15;
export const ZOOM_PUNCT_EXACT = 17;
export const ZOOM_PUNCT_APROX = 16;

/** Ce s-ar încadra e deja pe ecran, și destul de aproape ca să se lucreze pe el?
 *
 *  DE CE E O ÎNTREBARE, nu un `if` în efect. Încadrarea automată e singurul loc
 *  din ecran care poate arunca panoramarea omului, iar greșeala e tăcută în
 *  amândouă sensurile: prea strictă și harta nu mai găsește niciodată parcela,
 *  prea largă și fiecare salvare smucește cadrul. Nici una nu se vede citind
 *  codul.
 *
 *  NU DOAR «în cadru»: pe toată Bucureștiul parcela e în cadru și are opt
 *  pixeli. De aceea se cere ȘI o mărire din care chiar se poate lucra, aceeași
 *  pe care ar alege-o încadrarea.
 *
 *  `cadru` e `[[sud, vest], [nord, est]]`, ca `LatLngBounds` al lui Leaflet. */
export function eDejaInCadru(
  cadru: [[number, number], [number, number]] | null,
  zoom: number,
  contur: [number, number][] | null | undefined,
  punct: [number, number] | null,
  aproximativ: boolean,
): boolean {
  if (!cadru) return false;
  const [[sud, vest], [nord, est]] = cadru;
  const inauntru = (p: [number, number]) =>
    p[0] >= sud && p[0] <= nord && p[1] >= vest && p[1] <= est;
  if (contur && contur.length >= 3) {
    return contur.every(inauntru) && zoom >= ZOOM_CONTUR_MIN;
  }
  if (!punct) return false;
  return inauntru(punct)
      && zoom >= (aproximativ ? ZOOM_PUNCT_APROX : ZOOM_PUNCT_EXACT);
}

export function lungime(a: [number, number], b: [number, number]): number {
  const lat0 = ((a[0] + b[0]) / 2) * Math.PI / 180;
  const mx = 111320 * Math.cos(lat0);
  const my = 111132.92 - 559.82 * Math.cos(2 * lat0) + 1.175 * Math.cos(4 * lat0);
  return Math.hypot((b[1] - a[1]) * mx, (b[0] - a[0]) * my);
}

// Cota unei laturi, scrisă ca pe o planșă: metri cu o zecimală.
//
// O zecimală, nu două: un contur tras cu mâna pe hartă are o precizie de vreo
// jumătate de metru, iar „94,37 m" ar promite un centimetru pe care nimeni nu
// l-a măsurat. Unde conturul VINE dintr-un topo, cifra exactă e oricum în
// planșă, nu aici.
export function cota(metri: number): string {
  return tr("harta.metri", {
    metri: numar(metri, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
  });
}

// Încotro se scrie cota: în afara conturului, pe direcția de la centru spre
// mijlocul laturii.
//
// Nu pe mijlocul laturii: acolo stă mânerul din care se naște un colț nou, iar
// eticheta ar sta peste el — adică peste unealtă. Direcția se ia în reperul
// ecranului (x spre est, y în JOS), cu corecția de latitudine, ca eticheta unei
// laturi orizontale să iasă chiar pe verticală.
export function inafara(pts: [number, number][], i: number): [number, number] {
  const a = pts[i], b = pts[(i + 1) % pts.length];
  const lat0 = (pts.reduce((s, p) => s + p[0], 0) / pts.length) * Math.PI / 180;
  const kx = Math.cos(lat0);
  const cx = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const cy = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const dx = ((a[1] + b[1]) / 2 - cx) * kx;
  const dy = -((a[0] + b[0]) / 2 - cy);
  const n = Math.hypot(dx, dy) || 1;
  return [dx / n, dy / n];
}

// Cel mai apropiat punct de pe conturul parcelei.
//
// Capetele unei zone de acces trebuie să fie PE contur: adaptorul le lipește
// dacă sunt în trei metri și lasă zona deoparte dacă sunt mai departe, deci
// lipirea se face aici, unde omul vede unde a căzut, nu la generare.
//
// Măsurat punct-segment, într-un reper local în metri (aceeași corecție de
// latitudine ca la `arie`): în grade, un grad de longitudine e mai scurt decât
// unul de latitudine, iar „cel mai aproape" ar minți cu vreo 30 % în România.
export function lipesteLaContur(pts: [number, number][],
                                p: [number, number]): [number, number] | null {
  if (pts.length < 3) return null;
  const lat0 = (pts.reduce((s, q) => s + q[0], 0) / pts.length) * Math.PI / 180;
  const kx = Math.cos(lat0);
  let bun: [number, number] | null = null;
  let dist = Infinity;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    const ax = a[1] * kx, ay = a[0], bx = b[1] * kx, by = b[0];
    const px = p[1] * kx, py = p[0];
    const dx = bx - ax, dy = by - ay;
    const lung = dx * dx + dy * dy;
    const t = lung === 0 ? 0
      : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lung));
    const qx = ax + t * dx, qy = ay + t * dy;
    const d = (px - qx) ** 2 + (py - qy) ** 2;
    if (d < dist) {
      dist = d;
      bun = [qy, qx / kx];
    }
  }
  return bun;
}

// Se taie poligonul pe el însuși? Un contur „papion" are arie, dar aria aia nu
// înseamnă nimic — laturile se anulează între ele. Trebuie spus, nu salvat.
function seTaie(pts: [number, number][]): boolean {
  const n = pts.length;
  if (n < 4) return false;
  const cruce = (o: [number, number], a: [number, number], b: [number, number]) =>
    (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);
  const taie = (p1: [number, number], p2: [number, number],
                p3: [number, number], p4: [number, number]) => {
    const d1 = cruce(p3, p4, p1), d2 = cruce(p3, p4, p2);
    const d3 = cruce(p1, p2, p3), d4 = cruce(p1, p2, p4);
    return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0))
        && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
  };
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // Laturile vecine se ating la capăt prin construcție — nu e o tăiere.
      if (j === i || (j + 1) % n === i || (i + 1) % n === j) continue;
      if (taie(pts[i], pts[(i + 1) % n], pts[j], pts[(j + 1) % n])) return true;
    }
  }
  return false;
}

export interface HartaProps {
  contur: [number, number][] | null;
  lat: number | null;
  lon: number | null;
  /** Zona edificabilă, ca multipoligon [lat, lon] — v. `edificabil.ts`. Se
   *  desenează hașurat, SUB contur. Cât se redesenează parcela nu apare: ar fi
   *  calculată pe conturul salvat, nu pe cel din mână. */
  edificabil?: [number, number][][][] | null;
  // Suprafața din acte, ca desenul să se poată verifica singur: „ai desenat
  // 27 300 m², actele spun 27 314". Fără ea, un contur desenat aiurea arată la
  // fel de bine ca unul corect.
  suprafataAsteptata?: number | null;
  // Modul de editare, controlat de părinte, ca butoanele plutitoare ale
  // machetei să stea unde le-a desenat macheta.
  mod: "vezi" | "pin" | "parcela" | "zone" | "retrageri" | "limita";
  /** ZONELE DE REGIM ale terenului (UTR Lc + UTR M1 pe același lot), fiecare
   *  cu conturul ei când se știe: umplere ușoară în culoarea zonei și
   *  eticheta cu numele și aria, SUB contur. Cea activă (al cărei regim merge
   *  la generare) are conturul mai apăsat. V. `ZonaUrbanistica`. */
  zoneRegim?: { nume: string; contur_wgs84: [number, number][] | null;
                suprafata_mp: number | null; activa: boolean }[] | null;
  /** ZONA AL CĂREI TAB E DESCHIS în panou: se vopsește mai apăsat, ca omul să
   *  vadă pe teren la ce se uită în cifre. */
  zonaEvidentiata?: string | null;
  /** UNDE SE DESENEAZĂ INTERDICȚIA: conturul zonei de regim al cărei tab e
   *  deschis, când retragerile arătate sunt ale ei; altfel tot lotul. Fără
   *  asta, cealaltă zonă ieșea toată roșie, ca și cum nu s-ar putea construi. */
  interdictieIn?: [number, number][] | null;
  /** LIMITA ÎN LUCRU, în modul «limita»: linia dintre zona editată și restul
   *  lotului, cu un punct de partea zonei. Harta o desenează cu două mânere
   *  pe contur și vopsește bucata de partea cerută; la fiecare eliberare
   *  cheamă `onLimita` cu linia nouă, iar părintele o ține ca ciornă. */
  limita?: (Linie & { zona: string }) | null;
  onLimita?: (linie: Linie) => void;
  /** BUCĂȚILE LOTULUI, fiecare cu numărul ei cadastral: un teren cumpărat în
   *  mai multe acte. Se desenează ca linii subțiri PESTE contur, cu numărul
   *  scris în mijloc — conturul rămâne unul singur (reuniunea lor), fiindcă
   *  el e ce se autorizează, dar omul trebuie să vadă din ce e făcut. */
  bucati?: { numar_cadastral: string | null; suprafata_mp: number;
             contur_wgs84: [number, number][] | null }[] | null;
  /** DRUMURILE din acte, desenate ALTFEL decât bucățile lotului: hașurate gri,
   *  cu conturul punctat și numele arterei pe ele.
   *
   *  Nu sunt bucăți ale lotului — suprafața lor nu intră în el și nici în baza
   *  de calcul a POT —, dar se păstrează desenate: pe ele se sprijină
   *  retragerea de la aliniament. V. `Drum` din `dosarclient.ts`. */
  drumuri?: { numar_cadastral: string | null; nume: string | null;
              fel?: "cale_acces" | "drum" | null;
              mentiuni?: string | null; de_ce?: string | null;
              laturi?: number[] | null;
              contur_wgs84: [number, number][] | null }[] | null;
  /** PORȚIUNILE CU RETRAGERE PROPRIE, din punctele desenate în act: «între
   *  punctele 9-10 retragerea e 6,00 m».
   *
   *  Se desenează ca o îngroșare a conturului pe bucata cerută, cu metrii
   *  scriși pe ea. Hașura se adâncește oricum acolo (porțiunea taie conturul
   *  în `calculeazaZona`), dar singură ea nu spune CÂT se cere, nici că
   *  cerința vine dintr-un act și nu din regula generală. */
  retrageriZone?: { p0_wgs84: [number, number]; p1_wgs84: [number, number];
                    setback_m: number; text?: string | null }[] | null;
  /** COTELE DE RETRAGERE: conturul pe care s-au aplicat retragerile, și cât
   *  cere fiecare latură a lui.
   *
   *  Se desenează ca o cotă de arhitect — o linie perpendiculară pe limită,
   *  lungă exact cât retragerea, cu capete și cu cifra pe ea. Lungimile
   *  laturilor rămân doar cât se desenează conturul: acolo sunt unealta cu care
   *  omul potrivește planul, pe când în rest întrebarea e «cât trebuie să mă
   *  retrag», nu «cât are latura». */
  coteContur?: [number, number][] | null;
  coteRetrageri?: number[] | null;
  /** DE CE e cifra cea care e, pe fiecare bucată a conturului tăiat: baza,
   *  porţiunile din act, mâna omului. Tooltip pe cifra din bandă. */
  coteSurse?: string[] | null;
  /** Retragerea aplicată și cea cerută pe fiecare latură a conturului SALVAT —
   *  pe ele se fac secțiunile, cifrele și mânerele. */
  pePunct?: { aplicat: number[]; cerut: number[] } | null;
  /** Cât CER actele pe fiecare latură, pe lângă cât se aplică. Unde se aplică
   *  mai puțin, fâșia dintre ele se hașurează galben: omul a tras-o cu mâna
   *  lui și are voie să încerce, dar trebuie să vadă ce nu se poate autoriza. */
  coteCerute?: number[] | null;
  /** O retragere trasă pe hartă: TOATE laturile feței și cât s-a cerut pe ea.
   *
   *  Laturile vin împreună, nu una câte una: o față are o lățime, iar N salvări
   *  pornite deodată s-ar bate între ele — fiecare pleacă de la ce era în
   *  ecran, deci ar rămâne numai ultima. */
  onRetragere?: (laturi: number[], metri: number) => void;
  // Laturile conturului, cu felul lor: cele la stradă se desenează distinct,
  // cu numele arterei.
  laturi?: Latura[];
  // Zonele de acces, desenate mereu și editabile în modul „zone".
  zone?: ZonaAcces[];
  /** MODUL «MUTĂ TERENUL» LUCREAZĂ PE O CIORNĂ, ca retragerile: pinul, conturul
   *  și tot ce stă pe el (banda, zona edificabilă, cotele, porțile) se mută și
   *  se rotesc pe loc, sub mână, iar serverul nu află nimic până la «Salvează
   *  terenul». Înainte se salva la fiecare eliberare a pinului: salvarea
   *  reface laturile, fronturile și drumurile OSM pe conturul nou, deci ținea
   *  secunde, iar până se întorcea omul vedea pinul mutat și terenul pe loc —
   *  «e nevoie de refresh să se așeze» (Oana, 28.10.2026).
   *
   *  `onMutare(dLat, dLon, grade)`: cât s-a translatat (în grade de latitudine
   *  și longitudine) și cât s-a rotit (sens trigonometric pe hartă) față de
   *  ce era salvat; rotirea e în jurul centrului conturului și o suferă și
   *  pinul. `comenzi` primește, cât ține modul, rotirea fină și salvarea, ca
   *  butoanele din panoul părintelui să lucreze pe aceeași ciornă. */
  onMutare?: (dLat: number, dLon: number, grade: number) => void;
  comenzi?: MutableRefObject<ComenziMutare | null>;
  /** Conturul se poate și ROTI (mânerul din dreapta lui): doar când poziția e
   *  o potrivire, nu o măsurătoare — un contur în Stereo 70 din acte nu se
   *  rotește de mână. */
  rotibil?: boolean;
  /** O muncă în curs peste hartă (textul ei): salvarea mutării ţine secunde,
   *  iar o hartă nemişcată între timp «dă impresia că a fost o problemă». */
  ocupat?: string | null;
  onParcela?: (contur: [number, number][]) => void;
  onZone?: (zone: ZonaAcces[]) => void;
}

export interface ComenziMutare {
  /** Rotește ciorna cu încă `grade` (pozitiv = spre stânga). */
  roteste: (grade: number) => void;
  /** Dă ciorna părintelui, prin `onMutare`. */
  salveaza: () => void;
}

export default function Harta({ contur, lat, lon, edificabil,
                                suprafataAsteptata, bucati, drumuri,
                                retrageriZone, coteContur, coteRetrageri,
                                coteSurse, coteCerute, pePunct, onRetragere,
                               mod, laturi, zone, onMutare, comenzi, onParcela,
                               onZone, rotibil, ocupat, zoneRegim, limita,
                               onLimita, zonaEvidentiata, interdictieIn }: HartaProps) {
  const t = useT();
  const tn = useTn();
  const gazda = useRef<HTMLDivElement>(null);
  const harta = useRef<L.Map | null>(null);
  const stratul = useRef<L.LayerGroup | null>(null);
  const schita = useRef<[number, number][]>([]);
  // Zonele la care se lucrează, și primul capăt al celei în curs. Ca și
  // schița conturului, stau într-un ref: o salvare întoarsă din rețea la
  // jumătatea marcării ar reconstrui stratul, adică i-ar lua omului capătul
  // de sub mouse.
  const zoneLucru = useRef<ZonaAcces[]>([]);
  const capat = useRef<[number, number] | null>(null);
  // Ce n-a mers la ultimul clic (al doilea capăt pe altă latură): se spune în
  // panou, nu se refuză în tăcere.
  const [refuz, setRefuz] = useState("");
  // Poligonul și mânerele de mijloc, ținute ca referințe fiindcă în timpul unei
  // trageri se mută PE LOC. O reconstrucție ar șterge exact markerul pe care
  // omul îl ține cu mouse-ul, iar tragerea s-ar rupe după primul pixel.
  const poligonul = useRef<L.Polygon | L.Polyline | null>(null);
  const dale = useRef<L.TileLayer | null>(null);
  const mijloacele = useRef<L.Marker[]>([]);
  // Cotele laturilor, ținute pe laturi ca mijloacele: cât se trage un colț, se
  // mută și se rescriu PE LOC. O reconstrucție ar rupe tragerea (vezi mai jos).
  const cotele = useRef<L.Marker[]>([]);
  const [cfg, setCfg] = useState<ConfigHarta | null>(null);
  const [eroare, setEroare] = useState("");
  // Două semnale, nu unul. `structura` reconstruiește desenul — se schimbă doar
  // când apare sau dispare un colț. `puls` reîmprospătează DOAR cifrele din
  // panou, în timpul tragerii; dacă ar intra în dependențele desenului, ar
  // reconstrui mânerele la fiecare pixel (vezi mai sus de ce nu merge).
  const [structura, setStructura] = useState(0);
  const [, setPuls] = useState(0);
  // Se adaugă colțuri la clic pe hartă? Doar cât timp desenăm unul NOU. Când
  // s-a plecat de la conturul existent, un clic rătăcit pe hartă ar arunca un
  // colț în celălalt capăt al cartierului.
  const [deseneaza, setDeseneaza] = useState(false);
  // Harta trăiește într-un `ref`, iar un `ref` nu declanșează randări. Fără
  // steagul ăsta în dependențe, efectul de desen rulează O DATĂ, înainte ca
  // harta să existe (configurația vine de pe rețea, deci întotdeauna după
  // prima randare), iese pe `if (!m) return` și nu mai e chemat niciodată:
  // conturul se încarcă, se așază în stare, și nu se desenează nimic.
  const [viu, setViu] = useState(false);

  useEffect(() => {
    configHarta().then(setCfg).catch((e) => setEroare(String(e)));
  }, []);

  // Încadrarea, ținută ca funcție ca s-o poată reface observatorul de
  // dimensiune, și un semn că s-a făcut o dată la o dimensiune credibilă.
  const incadreaza = useRef<(() => void) | null>(null);
  const incadrat = useRef(false);

  // Crearea hărții — o singură dată, după ce știm de unde vin dalele.
  useEffect(() => {
    if (!cfg || !gazda.current || harta.current) return;
    const m = L.map(gazda.current, {
      center: ROMANIA, zoom: 7, zoomControl: true, attributionControl: true,
      maxZoom: ZOOM_MAX,
    });
    dale.current = L.tileLayer(cfg.tile_url, {
      // SE POATE MERI MAI DEPARTE DECÂT ARE FURNIZORUL DALE.
      //
      // `max_zoom` e ultimul nivel la care furnizorul CHIAR are imagini (19 la
      // OpenStreetMap, până la 22 la Google). Pus pe `maxZoom`, el oprea și
      // apropierea: pe o parcelă de câteva zeci de metri, retragerile de 3–5 m
      // se desenează la câțiva pixeli și nu se poate lucra pe ele.
      //
      // `maxNativeZoom` spune de unde se CER dalele, `maxZoom` cât de aproape
      // se poate ajunge: peste ultimul nivel cu imagini, Leaflet mărește ultima
      // dală în loc să ceară una care ar răspunde 404. Fundalul se vede mai
      // moale, dar desenul nostru — conturul, hașura, cotele — rămâne vectorial
      // și deci clar, iar el e ce se măsoară.
      maxNativeZoom: cfg.max_zoom, maxZoom: ZOOM_MAX,
      attribution: cfg.attribution,
    }).addTo(m);
    // Fără prefixul „Leaflet".
    //
    // Atribuirea care rămâne e cea care CHIAR se cere: Google o impune pe orice
    // imagine cu dalele lor, OpenStreetMap o cere prin ODbL. Prefixul e
    // biblioteca făcându-și reclamă — licența ei (BSD-2) nu-l cere pe ecran, și
    // pe un cartonaș de 200 px lățime fură din singurul rând care contează.
    m.attributionControl.setPrefix(false);
    stratul.current = L.layerGroup().addTo(m);
    harta.current = m;
    setViu(true);
  }, [cfg]);

  // Observatorul de dimensiune, în efectul LUI.
  //
  // Părintele e un flex care se așază DUPĂ prima randare. Un `setTimeout(0)` nu
  // ajunge: `fitBounds` calculează zoomul din dimensiunea pe care Leaflet crede
  // că o are, iar dacă atunci e încă mică, rezultatul e zoom 0 — harta lumii,
  // cu parcela un punct în ea. Nu e o eroare, deci nimic nu se plânge.
  // Observatorul rezolvă cauza: la fiecare schimbare de dimensiune recalculăm,
  // iar încadrarea se reface DOAR dacă n-a apucat să se facă la o dimensiune
  // credibilă — altfel ne-am bate cu omul care tocmai a dat zoom.
  //
  // De ce separat de crearea hărții: acela iese devreme când harta există deja
  // („if (harta.current) return"). Sub `StrictMode`, React montează efectele de
  // DOUĂ ori — prima curățare deconecta observatorul, iar a doua rulare ieșea
  // pe garda aia fără să-l mai facă. Rămânea un observator mort, și cu el o
  // hartă care, dacă prima încadrare pica fiindcă panoul nu se așezase încă,
  // rămânea pe România pentru totdeauna.
  useEffect(() => {
    const m = harta.current, el = gazda.current;
    if (!m || !el) return undefined;
    const observator = new ResizeObserver(() => {
      m.invalidateSize();
      if (!incadrat.current) incadreaza.current?.();
    });
    observator.observe(el);
    return () => observator.disconnect();
  }, [viu]);

  useEffect(() => () => { harta.current?.remove(); harta.current = null; }, []);

  // La schimbarea măririi se redesenează.
  //
  // Numai pentru cote: ele se scriu doar pe laturile care au loc pe ecran, iar
  // „are loc" depinde de mărire. Fără asta, cine dă zoom ca să vadă cota unei
  // laturi scurte dă zoom degeaba — eticheta a fost lăsată deoparte la mărirea
  // de dinainte și nimic n-o mai aduce.
  useEffect(() => {
    const m = harta.current;
    if (!m) return undefined;
    const cere = () => { if (!trage.current) setStructura((k) => k + 1); };
    m.on("zoomend", cere);
    return () => { m.off("zoomend", cere); };
  }, [viu]);

  // Callback-urile părintelui, ținute într-un ref.
  //
  // `onPin` și `onParcela` sunt funcții-săgeată scrise direct în JSX-ul lui
  // `Localizare`, deci își schimbă identitatea la FIECARE randare a lui. Puse
  // în dependențele desenului, o salvare care se întoarce din rețea fix în
  // timpul unei trageri reconstruia straturile — adică îi smulgea omului
  // markerul de sub mouse, la jumătatea gestului. Din ref, identitatea lor nu
  // mai atinge nimic.
  const apel = useRef({ onMutare, onParcela, onZone, onRetragere, onLimita });
  apel.current = { onMutare, onParcela, onZone, onRetragere, onLimita };

  // CIORNA MUTĂRII (v. `onMutare`) trăieşte peste redesenări. Desenul se
  // reconstruieşte la orice schimbare a datelor sau a părintelui — inclusiv
  // la «se salvează», care doar schimbă o stare — şi o ciornă ţinută în
  // efect s-ar pierde: terenul sărea înapoi pe poziţia veche cât ţinea
  // salvarea, «ca un bug» (Oana, 29.10.2026). Se goleşte abia când vine o
  // geometrie nouă de la server sau se iese din mod.
  const ciorna = useRef({ dLat: 0, dLng: 0, grade: 0 });
  useEffect(() => {
    ciorna.current = { dLat: 0, dLng: 0, grade: 0 };
  }, [contur, lat, lon, mod]);

  // Al câtelea colț a fost făcut ultima dată. „Șterge ultimul colț" înseamnă
  // pe acela — nu coada inelului. După ce s-a născut un colț dintr-un mijloc,
  // cele două nu mai sunt același lucru, iar butonul ștergea din altă parte a
  // conturului decât se uita omul.
  const ultimulAdaugat = useRef<number | null>(null);

  // Se trage chiar acum de un mâner?
  //
  // Cât ține gestul, desenul NU se reconstruiește, oricine ar cere-o. `contur`
  // e un tablou nou la fiecare citire, iar o salvare care se întoarce din rețea
  // la jumătatea unei trageri schimba dependențele efectului, care golea
  // stratul — adică ștergea exact markerul ținut cu mouse-ul. Garda asta
  // acoperă toate cauzele deodată, nu doar pe cea găsită azi; la eliberare,
  // `structura` cere oricum o reconstrucție.
  const trage = useRef(false);

  // Când a căzut ultimul clic pe hartă. Vezi garda din `pune`.
  const ultimulClic = useRef(0);

  // Închiderea conturului. Ținută aici, nu în efect, fiindcă o cheamă și
  // dublu-clicul pe hartă, și clicul pe primul colț. Stabilă: citește
  // callback-ul din ref, deci nu se reînregistrează la fiecare randare.
  const inchide = useCallback(() => {
    if (schita.current.length >= 3 && !seTaie(schita.current)) {
      apel.current.onParcela?.(schita.current);
    }
  }, []);

  // Geometria mutată PE LOC, cât ține o tragere: poligonul își schimbă
  // vârfurile și culoarea, iar cele două mijloace vecine colțului tras se mută
  // cu el. Fără partea a doua, mijloacele rămân agățate unde erau și latura
  // arată ruptă până la eliberarea mouse-ului.
  // SCHIȚA RETRAGERILOR, ca `schita` la parcelă: ce se trage acum, înainte de
  // salvare. Câte o valoare per latură a conturului salvat.
  const schitaRetrageri = useRef<number[]>([]);
  // Poligonul zonei edificabile și cel al zonei de peste limită, ținute ca
  // obiecte: la tragere li se rescriu punctele (`setLatLngs`), nu se refac —
  // la zeci de cadre pe secundă, a le reconstrui ar fi vizibil.
  const zonaPol = useRef<L.Polygon | null>(null);
  const pestePol = useRef<L.Polygon | null>(null);

  const potrivestePoligonul = useCallback(() => {
    const pts = schita.current;
    const p = poligonul.current;
    if (!p) return;
    p.setLatLngs(pts as L.LatLngExpression[]);
    const rau = seTaie(pts);
    p.setStyle({ color: rau ? "#b5323d" : "#0b7f92",
                 fillColor: rau ? "#b5323d" : "#0b7f92" });
    // Cotele, cât ține tragerea. Se rescrie textul în elementul care există
    // deja, nu se reface pictograma: la o tragere ies zeci de cadre pe secundă,
    // iar un `setIcon` pe fiecare ar reconstrui DOM-ul de fiecare dată.
    //
    // Doar cât timp numărul de laturi e cel de la construcție: la tragerea unui
    // MIJLOC se naște un vârf, laturile se renumerotează de acolo încolo, și
    // cotele rămase ar arăta lungimea altei laturi. Atunci se așteaptă
    // eliberarea, care reconstruiește tot.
    if (cotele.current.length !== pts.length) return;
    cotele.current.forEach((h, i) => {
      const a = pts[i], b = pts[(i + 1) % pts.length];
      if (!h || !a || !b) return;
      h.setLatLng([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
      const el = h.getElement()?.firstElementChild;
      if (el) el.textContent = cota(lungime(a, b));
    });
  }, []);

  // Poligonul ȘI cele două mijloace vecine colțului tras. Se cheamă doar când
  // se mută un COLȚ: atunci numărul de vârfuri nu se schimbă, deci laturile
  // păstrează numerele lor. La tragerea unui mijloc se naște un vârf nou și
  // laturile se renumerotează de acolo încolo — mijloacele rămase ar sări cu o
  // latură înapoi, așa că atunci se mișcă doar poligonul, iar ele se reașază la
  // eliberare, când se reconstruiește oricum tot.
  const potriveste = useCallback((coltMutat: number) => {
    potrivestePoligonul();
    const pts = schita.current;
    const n = pts.length;
    if (n < 2) return;
    [(coltMutat - 1 + n) % n, coltMutat].forEach((i) => {
      const h = mijloacele.current[i];
      const a = pts[i], b = pts[(i + 1) % n];
      if (h && a && b) h.setLatLng([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
    });
  }, [potrivestePoligonul]);

  // SCHIȚA RETRAGERILOR se încarcă la intrarea în mod, ca `schita` la parcelă:
  // de acolo încolo mâna lucrează pe ea, nu pe ce vine de la server.
  useEffect(() => {
    if (mod === "retrageri" && pePunct?.aplicat.length) {
      schitaRetrageri.current = [...pePunct.aplicat];
    }
  }, [mod, pePunct]);

  // Desenul: conturul, pinul, mânerele.
  useEffect(() => {
    const m = harta.current, g = stratul.current;
    if (!m || !g) return;
    if (trage.current) return;
    g.clearLayers();
    poligonul.current = null;
    mijloacele.current = [];
    cotele.current = [];

    const editez = mod === "parcela";
    const puncte = editez ? schita.current : (contur || []);
    const rau = editez && seTaie(puncte);
    const culoare = rau ? "#b5323d" : "#0b7f92";

    // BANDA RETRAGERII, COLORATĂ — nu hașurată.
    //
    // Hotărât pe 15.10.2026, după patru încercări cu cote și linii care ieșeau
    // mereu strâmbe: banda se vopsește, iar cifra se scrie ÎN ea, deci se
    // citește ca lățimea ei. Nu mai e nimic de aliniat, deci nici de aliniat
    // greșit — chiar clasa de greșeală care se tot repeta.
    //
    // Două straturi: lotul întreg în culoarea interdicției, iar peste el zona
    // edificabilă în culoarea hârtiei. Diferența dintre ele E banda, deci cele
    // două nu au cum să nu se potrivească.
    if (!editez && puncte.length >= 3) {
      const baza = interdictieIn && interdictieIn.length >= 3 ? interdictieIn : puncte;
      L.polygon(baza as L.LatLngExpression[], {
        stroke: false, fillColor: "#b5323d", fillOpacity: 0.14,
        interactive: false,
      }).addTo(g);
      // În modul retrageri zona se desenează VIE, mai jos (`zonaPol`): aici ar
      // fi cea salvată, deci două contururi peste care omul n-ar ști care e al
      // mâinii lui.
      for (const poly of (mod === "retrageri" ? [] : edificabil ?? [])) {
        if (!poly.length || poly[0].length < 3) continue;
        L.polygon(poly as L.LatLngExpression[][], {
          color: "#b5323d", weight: 1, opacity: 0.75, dashArray: "4 3",
          fillColor: "#ffffff", fillOpacity: 0.9, interactive: false,
        }).addTo(g);
      }
    }

    // ZONA DE PESTE LIMITĂ, GALBENĂ, PLINĂ. E o SUPRAFAȚĂ, nu un semn: atâția
    // metri pătrați nu se pot autoriza. Cât se trage e hașurată (omul încă se
    // poate răzgândi); după ce dă drumul rămâne plină, ca avertisment.
    if (!editez && coteContur && coteRetrageri && coteCerute) {
      for (const poly of zonaPesteLimita(coteContur, coteRetrageri, coteCerute)) {
        if (!poly.length || poly[0].length < 3) continue;
        L.polygon(poly as L.LatLngExpression[][], {
          color: "#a97614", weight: 2, opacity: 0.95,
          fillColor: "#fbe6b8", fillOpacity: 0.9, interactive: false,
        }).addTo(g);
      }
    }

    // CIFRA FIECĂREI FEȚE, scrisă ÎN banda ei.
    //
    // Pe conturul TĂIAT pe porțiuni (`coteContur`), nu pe cel salvat: pe o
    // latură pe care actul cere 5 m pe o bucată, cifra bucății e 5, iar a
    // restului e cea generală. Scrise pe latura salvată, cifrele erau ale
    // regulii generale pe toată latura — «5,0 m» lângă «22,5 m» pe aceeași
    // latură (Ghencea 91D, 29.10.2026), deși banda hașurată avea 5.
    //
    // Indicii secțiunilor sunt pe conturul din care s-au făcut (`peSectiuni`),
    // nu pe cel salvat: cel tăiat are mai multe vârfuri, iar un indice al lui
    // pe conturul salvat cădea în gol și dărâma ecranul (29.10.2026).
    const peSectiuni = (coteContur && coteContur.length >= 3 && coteRetrageri?.length)
      ? coteContur : puncte;
    const sectiuni = (peSectiuni === coteContur && coteContur && coteRetrageri)
      ? sectiuniContur(coteContur, coteRetrageri, coteCerute ?? coteRetrageri)
      : (puncte.length >= 3 && pePunct?.aplicat.length)
        ? sectiuniContur(puncte, pePunct.aplicat, pePunct.cerut)
        : [];
    for (const sec of (editez || mod === "retrageri" ? [] : sectiuni)) {
      if (!(sec.setback_m > 0)) continue;
      const i0 = sec.laturi[0];
      const i1 = sec.laturi[sec.laturi.length - 1];
      const a0 = peSectiuni[i0], b0 = peSectiuni[(i1 + 1) % peSectiuni.length];
      if (!a0 || !b0) continue;
      const pa = m.latLngToContainerPoint(a0 as L.LatLngExpression);
      const pb = m.latLngToContainerPoint(b0 as L.LatLngExpression);
      if (pa.distanceTo(pb) < 34) continue;
      const sub = sec.cerut_m > sec.setback_m + 1e-6;
      // La jumătatea benzii: acolo cifra se citește ca lățimea ei.
      const q: [number, number] = [
        sec.mijloc[0] + sec.normala[0] * sec.setback_m * 0.5,
        sec.mijloc[1] + sec.normala[1] * sec.setback_m * 0.5,
      ];
      const explicatie = peSectiuni === coteContur ? coteSurse?.[i0] : null;
      const eticheta = L.marker(q as L.LatLngExpression, {
        interactive: !!explicatie, keyboard: false, zIndexOffset: 750,
        icon: L.divIcon({
          className: "ob-cota-cutie",
          html: `<span class="ob-cota-retragere${sub ? " sub" : ""}">`
                + `${cota(sec.setback_m)}</span>`,
          iconSize: [44, 15], iconAnchor: [22, 7],
        }),
      }).addTo(g);
      // DE CE ATÂT: la trecerea cu mouse-ul, ce s-a luat în calcul pentru
      // cifra asta — baza, porţiunile din act, categoria străzii, mâna omului.
      if (explicatie) eticheta.bindTooltip(explicatie, { direction: "top", offset: [0, -8] });
    }

    // MÂNERELE RETRAGERILOR — același mecanism ca la «Redesenează parcela».
    //
    // Cerut pe 15.10.2026: «make it like the Redeseneaza parcela, same
    // mechanism». Acolo mânerele stau PE FORMA care se editează, iar forma se
    // reface sub mână; aici forma e zona edificabilă, iar mânerul fiecărei fețe
    // stă pe muchia ei. Tras spre interior, retragerea crește.
    //
    // ȘI DE CE ASTA REPARĂ „liniile ies din parcelă": previzualizarea de dinainte
    // era un patrulater desenat pe normală, care nu știa nimic despre lot și îi
    // ieșea în afară la orice colț. Acum se redesenează CHIAR zona edificabilă,
    // tăiată din lot prin construcție — nu poate ieși din el.
    if (mod === "retrageri" && apel.current.onRetragere && sectiuni.length
        && puncte.length >= 3) {
      const cerut = pePunct?.cerut ?? [];
      const refaZona = () => {
        const z = zonaEdificabila(puncte, schitaRetrageri.current);
        if (zonaPol.current) {
          zonaPol.current.setLatLngs(
            (z.length ? z[0] : []) as L.LatLngExpression[][]);
        }
        const peste = zonaPesteLimita(puncte, schitaRetrageri.current, cerut);
        if (pestePol.current) {
          pestePol.current.setLatLngs(
            (peste.length ? peste[0] : []) as L.LatLngExpression[][]);
        }
      };
      const z0 = zonaEdificabila(puncte, schitaRetrageri.current);
      zonaPol.current = L.polygon((z0.length ? z0[0] : []) as L.LatLngExpression[][], {
        color: "#b5323d", weight: 1.5, dashArray: "4 3",
        fillColor: "#ffffff", fillOpacity: 0.9, interactive: false,
      }).addTo(g);
      const p0 = zonaPesteLimita(puncte, schitaRetrageri.current, cerut);
      pestePol.current = L.polygon((p0.length ? p0[0] : []) as L.LatLngExpression[][], {
        color: "#a97614", weight: 2, fillColor: "#fbe6b8", fillOpacity: 0.85,
        interactive: false,
      }).addTo(g);

      // PUNCTELE SE CITESC DE PE LINIE, nu se construiesc (v.
      // `puncteleRetragerilor`). O față înghițită de vecine n-are linie, deci
      // n-are nici mâner: ar fi un mâner care nu arată nimic.
      const peLinie = puncteleRetragerilor(puncte, schitaRetrageri.current,
                                           sectiuni);
      sectiuni.forEach((sec, si) => {
        const start = peLinie[si];
        if (!start) return;
        // ANCORA: locul de pe limită sub punctul de pe linie. Mânerul pleacă de
        // acolo și merge NUMAI perpendicular — retragerea e o adâncime, deci
        // are un singur grad de libertate, iar mânerul ei trebuie să aibă tot
        // unul. V. `ancoraFetei`.
        const mij = ancoraFetei(puncte, sec, start);
        const [nLat, nLon] = sec.normala;
        const laMetri = (metri: number): [number, number] =>
          [mij[0] + nLat * metri, mij[1] + nLon * metri];
        const d0 = schitaRetrageri.current[sec.laturi[0]] ?? sec.setback_m;
        const h = L.marker(laMetri(d0) as L.LatLngExpression, {
          icon: COLT, draggable: true, keyboard: false, zIndexOffset: 950,
        }).addTo(g);
        const eticheta = L.marker(laMetri(d0 / 2) as L.LatLngExpression, {
          interactive: false, keyboard: false, zIndexOffset: 760,
          icon: L.divIcon({
            className: "ob-cota-cutie",
            html: `<span class="ob-cota-retragere">${cota(d0)}</span>`,
            iconSize: [44, 15], iconAnchor: [22, 7],
          }),
        }).addTo(g);
        const metriDin = (q: L.LatLng) => {
          const kLat = 111132.92;
          const kLon = 111320 * Math.cos(mij[0] * Math.PI / 180);
          const vx = (q.lng - mij[1]) * kLon, vy = (q.lat - mij[0]) * kLat;
          const ux = nLon * kLon, uy = nLat * kLat;
          const n2 = Math.hypot(ux, uy) || 1;
          return Math.max(0, Math.min(60, (vx * ux + vy * uy) / n2));
        };
        let ultimulBun = d0;
        h.on("dragstart", () => { trage.current = true; });
        h.on("drag", (ev) => {
          const mk = ev.target as L.Marker;
          const cerutAcum = metriDin(mk.getLatLng());
          const inainte = sec.laturi.map((i) => schitaRetrageri.current[i]);
          for (const i of sec.laturi) schitaRetrageri.current[i] = cerutAcum;
          // CLAMP: nu se poate trage până dispare zona. Dacă s-a stins, se dă
          // înapoi la ultima adâncime la care mai exista ceva de construit —
          // altfel mânerul ar rămâne fără linie chiar sub deget.
          const z = zonaEdificabila(puncte, schitaRetrageri.current);
          let metri = cerutAcum;
          if (!z.length) {
            metri = ultimulBun;
            sec.laturi.forEach((i, k) => { schitaRetrageri.current[i] = inainte[k]; });
            for (const i of sec.laturi) schitaRetrageri.current[i] = metri;
          } else {
            ultimulBun = metri;
          }
          refaZona();
          // NUMAI PE NORMALĂ, din ancoră: alunecarea în lungul feței nu mișcă
          // mânerul, fiindcă nu schimbă nici retragerea. Așa se poate nimeri
          // adâncimea voită fără să trebuiască ținută mâna pe o linie.
          mk.setLatLng(laMetri(metri) as L.LatLngExpression);
          eticheta.setLatLng(laMetri(metri / 2) as L.LatLngExpression);
          const el = eticheta.getElement()?.firstElementChild;
          const sub = (cerut[sec.laturi[0]] ?? 0) > metri + 1e-6;
          if (el) {
            el.textContent = cota(metri);
            el.classList.toggle("sub", sub);
          }
        });
        h.on("dragend", () => {
          trage.current = false;
          apel.current.onRetragere?.(
            sec.laturi, Math.round((schitaRetrageri.current[sec.laturi[0]] ?? 0) * 100) / 100);
        });
      });
    }

    if (puncte.length >= 3) {
      poligonul.current = L.polygon(puncte as L.LatLngExpression[], {
        color: culoare, weight: 2, dashArray: "6 4",
        fillColor: culoare, fillOpacity: 0.12,
      }).addTo(g);
      // BUCĂȚILE LOTULUI, peste conturul lui: un teren cumpărat în trei acte
      // se vede ca trei parcele, cu numărul cadastral scris pe fiecare. Linie
      // continuă subțire, fără umplere — conturul rămâne desenul principal,
      // iar astea spun din ce e făcut. Nu se desenează în timpul redesenării
      // (`editez`): atunci omul trage chiar conturul, iar bucățile ar fi
      // vechile acte peste mâna lui.
      if (!editez) {
        for (const b of bucati ?? []) {
          const p2 = (b.contur_wgs84 ?? []) as [number, number][];
          if (p2.length < 3) continue;
          L.polygon(p2 as L.LatLngExpression[], {
            color: culoare, weight: 1, opacity: 0.85, fill: false,
            interactive: false,
          }).addTo(g);
          if (b.numar_cadastral) {
            const c0: [number, number] = [
              p2.reduce((a, q) => a + q[0], 0) / p2.length,
              p2.reduce((a, q) => a + q[1], 0) / p2.length,
            ];
            L.marker(c0, {
              interactive: false,
              icon: L.divIcon({
                className: "ob-harta-parcela",
                html: `<span>${b.numar_cadastral}</span>`,
                iconSize: [0, 0],
              }),
            }).addTo(g);
          }
        }

        // DRUMURILE, hașurate gri. Trei semne, fiindcă unul singur n-ajunge:
        // hașura spune «nu se construiește», conturul punctat spune unde se
        // termină, iar plăcuța spune ce e. Bucățile lotului au linie continuă
        // și fără umplere — deci nu se pot confunda nici la alb-negru, nici de
        // cineva care nu deosebește griul de verde-albastru.
        for (const d of drumuri ?? []) {
          const p2 = (d.contur_wgs84 ?? []) as [number, number][];
          if (p2.length < 3) continue;
          // DRUMUL SE UMPLE, NU SE HAȘUREAZĂ.
          //
          // Prima formă îl hașura cu `hasura(..., 4, 45)` — exact pasul și
          // exact unghiul benzii interzise de deasupra. Două hașuri de 45° la
          // 4 px, una roșie la 0,4 și una gri la 0,55, se citesc de la distanța
          // de lucru ca ACEEAȘI textură: ochiul deosebește greu culoarea unor
          // linii subțiri, și cu atât mai greu pe un lot așezat oblic, unde
          // amândouă se rotesc la fel. «Nu se construiește aici» și «asta nu e
          // terenul tău» ar fi arătat la fel.
          //
          // Un gri plin nu e o altă hașură, e alt FEL de semn — singura
          // deosebire care ține și la alb-negru. Aceeași familie cu `road` din
          // paleta planșei A01, care e tot plin.
          // FĂRĂ CONTUR PUNCTAT. Linia drumului cădea exact peste limita
          // lotului — acolo unde cele două se ating —, iar două punctate una
          // peste alta se citeau ca o a treia linie, între drum și teren, care
          // nu există. Cerut scos pe 16.10.2026. Rămâne umplerea, care spune
          // destul: unde se termină albastrul începe terenul.
          L.polygon(p2 as L.LatLngExpression[], {
            stroke: false, fillColor: CULOARE_DRUM, fillOpacity: 0.3,
            interactive: false,
          }).addTo(g);
          // CALEA DE ACCES ÎȘI SPUNE NUMELE. Pe un drum oarecare rămâne
          // numele arterei sau numărul cadastral.
          //
          // `fel` vine de la import, dar e un câmp NOU: pe un dosar citit
          // înainte de 16.10.2026 lipsește, iar textul n-ar apărea până la o
          // recitire — care costă o deschidere de model pe fiecare act. Se
          // recunoaște deci și din mențiunea păstrată pe fișă, ori din motivul
          // pentru care bucata a fost luată drept drum.
          const scrieCaleAcces = d.fel === "cale_acces"
            || /cale\s+de\s+acces/i.test(`${d.mentiuni ?? ""} ${d.de_ce ?? ""}`);
          const eticheta = scrieCaleAcces
            ? t("loc.cale_acces") : (d.nume || d.numar_cadastral);
          // NUMELE SE SCRIE PE DRUM, ca pe o hartă de stradă: text simplu în
          // lungul fâșiei, fără plăcuță, vopsit pe ea — unde, cum și cât de
          // mare încape se socotește în `asezareaEtichetei`.
          //
          // Cerut pe 16.10.2026, în patru rânduri pe aceeași etichetă: «not in
          // a box (...) it overlaps with the lot itself», «static (...)
          // shouldn't move or get bigger when i zoom out», «make it so that
          // the text shrinks too (...) proportionally smaller when zooming
          // out», și «it should sit inside the road (...) this is general not
          // just for this example». Ce leagă toate patru: textul să se poarte
          // ca o VOPSEA pe drum — în el, la aceeași mărime față de el, la
          // orice zoom — nu ca o plăcuță pusă deasupra hărții.
          const asez = eticheta ? asezareaEtichetei(p2, eticheta.length) : null;
          if (eticheta && asez) {
            // Pixeli pe metru, de la hartă: cu ei se trece mărimea din metri
            // (cât încape pe drum) în pixeli de ecran.
            const A = L.latLng(asez.punct[0], asez.punct[1]);
            const B = L.latLng(asez.punct[0], asez.punct[1] + 0.001);
            const pxPerM = m.latLngToContainerPoint(A)
                            .distanceTo(m.latLngToContainerPoint(B))
                           / Math.max(m.distance(A, B), 1e-9);
            // Mărimea vine din geometrie, în METRI, și se înmulțește cu
            // pixelii pe metru: textul rămâne vopsit pe drum la orice mărire,
            // și e dovedit înăuntrul lui (v. `asezareaEtichetei`). Fără prag
            // de jos — un prag ar rupe proporția, iar la micșorare textul
            // ajunge o dungă de doi pixeli, adică exact cât se mai vede și
            // din drum.
            const fs = asez.inaltime_m * pxPerM;
            L.marker(asez.punct as L.LatLngExpression, {
              interactive: false,
              icon: L.divIcon({
                className: "ob-harta-drum",
                // Mărimea și rotirea vin de aici, fiindcă amândouă se
                // socotesc din desen; restul felului e în CSS, legat de `em`,
                // ca haloul și spațierea să se strângă odată cu litera.
                // `y` crește în jos pe ecran, deci unghiul se întoarce.
                html: `<span style="font-size:${fs.toFixed(2)}px;`
                    + `transform:rotate(${(-asez.unghi_deg).toFixed(1)}deg)">`
                    + `${scapa(eticheta)}</span>`,
                iconSize: [0, 0],
              }),
            }).addTo(g);
          }
        }

        // PORȚIUNILE CU RETRAGERE PROPRIE: conturul îngroșat pe bucata cerută.
        // Culoarea interdicției, fiindcă e chiar o interdicție mai adâncă —
        // dar pe linie, nu pe suprafață: suprafața o spune hașura, iar CIFRA
        // o spune banda ei (secțiunile de mai sus, pe conturul tăiat). Metrii
        // scriși și aici dublau cifra din bandă; ce cere actul rămâne în
        // tooltip.
        for (const z of retrageriZone ?? []) {
          if (!z.p0_wgs84 || !z.p1_wgs84) continue;
          L.polyline([z.p0_wgs84, z.p1_wgs84] as L.LatLngExpression[], {
            color: "#b5323d", weight: 5, opacity: 0.55,
          }).addTo(g).bindTooltip(
            z.text ? `${z.text}` : `${tr("harta.retragere_din_act")} ${cota(z.setback_m)}`,
            { sticky: true });
        }

        // ZONELE DE REGIM, sub contur: umplere ușoară și eticheta cu numele și
        // aria. Zona care se editează chiar acum nu se desenează din bază —
        // bucata ei VIE e mai jos, sub mâna omului.
        (zoneRegim ?? []).forEach((z, i) => {
          const p2 = (z.contur_wgs84 ?? []) as [number, number][];
          if (p2.length < 3 || (mod === "limita" && limita?.zona === z.nume)) return;
          const c = CULORI_ZONA_REGIM[i % CULORI_ZONA_REGIM.length];
          const evidentiata = z.nume === zonaEvidentiata;
          L.polygon(p2 as L.LatLngExpression[], {
            color: c, weight: evidentiata ? 3.5 : z.activa ? 2.5 : 1.5, opacity: 0.95,
            dashArray: evidentiata ? undefined : "2 5",
            fillColor: c, fillOpacity: evidentiata ? 0.34 : 0.13, interactive: false,
          }).addTo(g);
          const inima = inimaPoligonului(p2) ?? p2[0];
          L.marker(inima as L.LatLngExpression, {
            interactive: false, keyboard: false, zIndexOffset: 700,
            icon: L.divIcon({
              className: "ob-harta-zona",
              html: `<span style="border-color:${c};color:${c}">${scapa(
                tr("harta.zona_regim", { nume: z.nume,
                                         suprafata: numar(Math.round(z.suprafata_mp ?? 0)) }))}</span>`,
              iconSize: [0, 0],
            }),
          }).addTo(g);
        });

        // LIMITA UNEI ZONE, TRASĂ DE OM: linia între două puncte de pe contur,
        // cu câte un mâner la fiecare capăt, și bucata de partea zonei
        // vopsită. Mânerele alunecă NUMAI pe contur (`lipesteLaContur`): o
        // limită între zone pleacă de pe limita lotului și ajunge tot pe ea.
        // Tăierea de aici e previzualizarea; cea scrisă în dosar o face
        // serverul din aceeași linie, în Stereo 70.
        if (mod === "limita" && limita && apel.current.onLimita) {
          const ring = puncte as [number, number][];
          const idx = Math.max(0, (zoneRegim ?? []).findIndex((z) => z.nume === limita.zona));
          const c = CULORI_ZONA_REGIM[idx % CULORI_ZONA_REGIM.length];
          const lucru: Linie = { p0: limita.p0, p1: limita.p1, dePartea: limita.dePartea };
          const bucata0 = taiePeLinie(ring, lucru) ?? [];
          const bucata = L.polygon(bucata0 as L.LatLngExpression[], {
            color: c, weight: 2, opacity: 0.95, fillColor: c, fillOpacity: 0.3,
            interactive: false,
          }).addTo(g);
          const linie = L.polyline([lucru.p0, lucru.p1] as L.LatLngExpression[], {
            color: c, weight: 4, opacity: 0.95, dashArray: "10 6", interactive: false,
          }).addTo(g);
          const eticheta = L.marker((inimaPoligonului(bucata0) ?? lucru.p0) as L.LatLngExpression, {
            interactive: false, keyboard: false, zIndexOffset: 700,
            icon: L.divIcon({ className: "ob-harta-zona", iconSize: [0, 0],
                              html: `<span style="border-color:${c};color:${c}"></span>` }),
          }).addTo(g);
          const refa = () => {
            const b = taiePeLinie(ring, lucru) ?? [];
            bucata.setLatLngs(b as L.LatLngExpression[]);
            linie.setLatLngs([lucru.p0, lucru.p1] as L.LatLngExpression[]);
            if (b.length >= 3) eticheta.setLatLng((inimaPoligonului(b) ?? b[0]) as L.LatLngExpression);
            const el = eticheta.getElement()?.firstElementChild;
            if (el) {
              el.textContent = tr("harta.zona_regim", {
                nume: limita.zona, suprafata: numar(Math.round(b.length >= 3 ? ariaBucatii(b) : 0)) });
            }
          };
          refa();
          for (const capat of ["p0", "p1"] as const) {
            const h = L.marker(lucru[capat] as L.LatLngExpression, {
              icon: COLT, draggable: true, keyboard: false, zIndexOffset: 960,
            }).addTo(g);
            h.on("dragstart", () => { trage.current = true; });
            h.on("drag", (ev) => {
              const mk = ev.target as L.Marker;
              const q = mk.getLatLng();
              const pe = lipesteLaContur(ring, [q.lat, q.lng]);
              if (!pe) return;
              lucru[capat] = pe;
              mk.setLatLng(pe as L.LatLngExpression);
              refa();
            });
            h.on("dragend", () => {
              trage.current = false;
              apel.current.onLimita?.({ ...lucru });
            });
          }
        }
      }
    } else if (editez && puncte.length === 2) {
      // Cu două colțuri nu există poligon, dar există o latură — iar fără ea
      // primul segment desenat nu se vede deloc.
      poligonul.current = L.polyline(puncte as L.LatLngExpression[], {
        color: culoare, weight: 2, dashArray: "6 4",
      }).addTo(g);
    }

    // COTELE LATURILOR.
    //
    // Un contur desenat pe hartă n-are nicio cifră pe el: se vede unde e lotul,
    // dar nu cât are latura — iar cine desenează are în mână un plan cu cote,
    // pe care vrea să le potrivească. Aria singură nu ajunge: două contururi cu
    // aceeași arie pot avea laturi cu totul diferite.
    //
    // Se scriu în ORICE mod, nu doar cât se desenează: sunt o măsură a lotului,
    // ca aria, nu o unealtă de editare. Dar numai pe laturile care au loc pe
    // ecran — v. mai jos.
    if (puncte.length >= 2 && editez) {
      puncte.forEach((a, i) => {
        // Cu două puncte există o singură latură, nu un inel: ultima „latură"
        // (de la al doilea la primul) nu e desenată, deci n-are ce cota.
        if (puncte.length === 2 && i === 1) return;
        const b = puncte[(i + 1) % puncte.length];
        // Sub vreo treizeci de pixeli eticheta e mai lungă decât latura: pe un
        // contur de topo cu treizeci de vârfuri, cotele s-ar așeza una peste
        // alta și n-ar mai spune niciuna nimic. Cine vrea cifra dă zoom, iar
        // reconstrucția de la `zoomend` o aduce.
        const pa = m.latLngToContainerPoint(a as L.LatLngExpression);
        const pb = m.latLngToContainerPoint(b as L.LatLngExpression);
        if (pa.distanceTo(pb) < 30) return;
        const [dx, dy] = puncte.length >= 3 ? inafara(puncte, i) : [0, -1];
        const h = L.marker([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], {
          // Nu se poate da clic pe ele: în modul „zone", un clic pe cotă ar
          // fi un clic care nu pune capătul zonei, fără să se vadă de ce.
          interactive: false, keyboard: false, zIndexOffset: 700,
          icon: L.divIcon({
            className: "ob-cota-cutie",
            html: `<span class="ob-cota">${cota(lungime(a, b))}</span>`,
            // Caseta e strânsă pe text („365,4 m" la 10,5 px monospace), și
            // asta contează: ancora de mai jos mută eticheta cu JUMĂTATE din
            // lățimea casetei plus o gardă, iar o casetă largă ar trimite cota
            // laturilor verticale tocmai în cartierul vecin. Un text mai lung
            // decât caseta iese din ea în amândouă părțile, deci rămâne
            // centrat pe același punct.
            iconSize: [56, 16],
            // ÎN AFARA conturului, pe direcția laturii, și complet în afara
            // lucrurilor care stau chiar pe mijlocul ei: mânerul din care se
            // naște un colț (rază 7 px) și, mai lat, poarta de intrare (disc
            // de 26 px, deci 13 px în afara liniei). De aici distanțele: pe
            // orizontală jumătatea casetei plus 16 px, pe verticală jumătatea
            // ei plus 14 — prima trebuie să treacă și de lățimea casetei.
            iconAnchor: [28 - dx * 44, 8 - dy * 22],
          }),
        }).addTo(g);
        cotele.current[i] = h;
      });
    }

    if (editez) {
      puncte.forEach((pt, i) => {
        const h = L.marker(pt as L.LatLngExpression, {
          icon: COLT, draggable: true, keyboard: false, zIndexOffset: 900,
        }).addTo(g);
        h.on("dragstart", () => { trage.current = true; });
        h.on("drag", (ev) => {
          const q = (ev.target as L.Marker).getLatLng();
          schita.current = schita.current.map(
            (r, j) => (j === i ? [q.lat, q.lng] as [number, number] : r));
          potriveste(i);
          setPuls((k) => k + 1);
        });
        // La eliberare se reconstruiește: abia atunci se pot muta în siguranță
        // mânerele și se poate reașeza ordinea straturilor.
        h.on("dragend", () => {
          trage.current = false;
          setStructura((k) => k + 1);
        });
        h.on("click", (ev) => {
          L.DomEvent.stopPropagation(ev as unknown as Event);
          // Clic pe primul colț = închide conturul. E convenția din orice
          // editor de hărți, și singura care nu cere să ghicești că
          // dublu-clicul face ceva.
          if (deseneaza && i === 0 && schita.current.length >= 3) inchide();
        });
        h.on("contextmenu", (ev) => {
          L.DomEvent.stopPropagation(ev as unknown as Event);
          // Sub trei colțuri nu mai e parcelă, deci ultimul nu se poate scoate.
          if (schita.current.length <= 3) return;
          schita.current = schita.current.filter((_, j) => j !== i);
          ultimulAdaugat.current = null;
          setStructura((k) => k + 1);
        });
        h.bindTooltip(deseneaza && i === 0 && puncte.length >= 3
          ? tr("harta.inchide_conturul")
          : tr("harta.trage_coltul"), { direction: "top", offset: [0, -10] });
      });

      // Mijloacele de latură — doar la AJUSTARE, nu și cât se desenează.
      //
      // Cât timp se pun colțuri, un clic care cade peste un mijloc era prins de
      // el: se năștea un colț la mijlocul laturii, nu sub cursor, unde se uita
      // omul. Două înțelesuri pentru același clic, dintre care unul greșit.
      puncte.forEach((pt, i) => {
        if (deseneaza || puncte.length < 3) return;
        const q = puncte[(i + 1) % puncte.length];
        const mij: [number, number] = [(pt[0] + q[0]) / 2, (pt[1] + q[1]) / 2];
        const h = L.marker(mij as L.LatLngExpression, {
          icon: MIJLOC, draggable: true, keyboard: false, zIndexOffset: 800,
        }).addTo(g);
        mijloacele.current[i] = h;
        // Colțul se naște la ÎNCEPUTUL tragerii, și tot markerul ăsta îl mută
        // mai departe — fără reconstrucție, care ar rupe tragerea în curs.
        let nascut = -1;
        h.on("dragstart", () => {
          trage.current = true;
          nascut = i + 1;
          schita.current = [...schita.current.slice(0, nascut), mij,
                            ...schita.current.slice(nascut)];
          potrivestePoligonul();
        });
        h.on("drag", (ev) => {
          if (nascut < 0) return;
          const q2 = (ev.target as L.Marker).getLatLng();
          schita.current = schita.current.map(
            (r, j) => (j === nascut ? [q2.lat, q2.lng] as [number, number] : r));
          potrivestePoligonul();
          setPuls((k) => k + 1);
        });
        h.on("dragend", () => {
          trage.current = false;
          ultimulAdaugat.current = nascut >= 0 ? nascut : null;
          nascut = -1;
          setStructura((k) => k + 1);
        });
        h.on("click", (ev) => {
          L.DomEvent.stopPropagation(ev as unknown as Event);
          schita.current = [...schita.current.slice(0, i + 1), mij,
                            ...schita.current.slice(i + 1)];
          ultimulAdaugat.current = i + 1;
          setStructura((k) => k + 1);
        });
        h.bindTooltip(tr("harta.adauga_colt"), { direction: "top", offset: [0, -9] });
      });
    }

    // STRĂZILE: laturile care dau spre o arteră, cu numele ei.
    //
    // Nu la redesenarea conturului: acolo laturile se mută, iar numele ar
    // rămâne pe unde nu mai e nimic. Eticheta doar pe laturile cu loc pe
    // ecran, ca și cotele.
    // LATURILE LIPITE DE UN DRUM nu primesc nici linia groasă, nici plăcuța:
    // drumul e desenat chiar acolo, plin, cu numele pe el. Două semne pentru
    // același lucru se citeau ca o a treia linie între drum și teren — chiar
    // «linia punctată albastră» scoasă pe 16.10.2026 (prima oară am scos
    // conturul drumului, dar groasa de aici rămăsese).
    // SE JUDECĂ PE GEOMETRIE, nu pe lista importului.
    //
    // Prima oară am sărit laturile pe care drumul le dă ca ale lui (`laturi`).
    // Dar o latură ajunge front stradal și dintr-o ZONĂ DE ACCES pusă pe ea —
    // pe dosarul de pe Șos. Fundeni, 11 laturi sunt stradă, iar drumul numește
    // doar 7. Celelalte patru rămâneau cu linia groasă, exact în lungul
    // drumului. Se întreabă deci desenul: trece latura pe lângă un drum?
    const inelele = (drumuri ?? [])
      .map((d) => (d.contur_wgs84 ?? []) as [number, number][])
      .filter((x) => x.length >= 3);
    // Amândouă semnele, reunite: lista importului (care măsoară altfel, de la
    // segment la poligon) și proba de pe desen. Pe dosarul real una dă 7
    // laturi și cealaltă 6; ce vrem e să nu rămână niciuna.
    const laturiDrum = new Set<number>(
      (drumuri ?? []).flatMap((d) => d.laturi ?? []));
    if (mod !== "parcela" && puncte.length >= 3 && laturi?.length) {
      puncte.forEach((a, i) => {
        const l = laturi[i];
        if (!l || l.tip !== "strada") return;
        if (laturiDrum.has(i) || laturaLangaPoligon(puncte, i, inelele)) return;
        const b = puncte[(i + 1) % puncte.length];
        L.polyline([a, b] as L.LatLngExpression[], {
          color: CULOARE_STRADA, weight: 6, opacity: 0.45, interactive: false,
        }).addTo(g);
        const pa = m.latLngToContainerPoint(a as L.LatLngExpression);
        const pb = m.latLngToContainerPoint(b as L.LatLngExpression);
        if (pa.distanceTo(pb) < 60) return;
        const [dx, dy] = inafara(puncte, i);
        L.marker([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], {
          interactive: false, keyboard: false, zIndexOffset: 690,
          icon: L.divIcon({
            className: "ob-cota-cutie",
            html: `<span class="ob-strada">${scapa(l.catre || tr("harta.strada"))}</span>`,
            iconSize: [80, 16],
            // Un rând mai în afară decât cota, ca să nu se calce cu ea.
            iconAnchor: [40 - dx * 72, 8 - dy * 44],
          }),
        }).addTo(g);
      });
    }

    // ZONELE DE ACCES: segmente pe contur, desenate ca orice dată a
    // proiectului, editabile doar în modul lor.
    //
    // Excepția e redesenarea conturului, unde NU se desenează: conturul de
    // sub ele se mută, iar serverul le reașază el pe conturul nou după
    // salvare (cele din acte se recalculează, cele manuale se re-lipesc).
    const marchez = mod === "zone";
    const listaZone = mod === "parcela" ? []
      : marchez ? zoneLucru.current : (zone || []);
    listaZone.forEach((z, i) => {
      if (!z.p0_wgs84 || !z.p1_wgs84) return;
      const exact = !!z.exact, manual = z.sursa === "manual";
      // ZONA DEDUSĂ CARE CADE PE UN DRUM NU SE DESENEAZĂ pe vederea normală.
      //
      // Asta era «linia punctată albastră dintre drum și teren» (16.10.2026),
      // a treia oară: nu conturul drumului și nu marcajul de front stradal, ci
      // chiar zonele de acces — 9 px, albastrul străzii, linie întreruptă. Pe
      // dosarul de pe Șos. Fundeni, patru din șapte cad pe drum (laturile 0,
      // 27, 28, 29), iar trei dintre ele au 71, 54 și 57 m: nu e o poartă, e
      // tot frontul — 194 m de dungă exact peste limita dintre drum și teren.
      // Drumul e desenat chiar acolo, plin, cu numele pe el; dunga de deasupra
      // nu mai spune nimic în plus.
      //
      // Numai cele DEDUSE. O zonă `exact` (cu coordonate în acte) sau una pusă
      // de om e o poartă anume, nu un front întreg presupus — aia se vede și
      // peste drum. Și în modul «zone» se desenează toate: acolo omul le
      // editează, deci trebuie să le vadă.
      //
      // Numai desenul, fără lista `laturi` a drumului (pe care o ia marcajul
      // de front stradal de mai sus): lista măsoară apropierea cea mai mare,
      // deci cuprinde și o latură care atinge drumul doar cu un capăt. Pe
      // dosarul real, latura 1 e în listă, dar mijlocul ei e la 9,8 m de drum
      // și pleacă spre vecin — dunga de acolo chiar nu stă între drum și
      // teren.
      if (!marchez && !exact && !manual
          && segmentLangaPoligon(z.p0_wgs84, z.p1_wgs84, inelele)) return;
      const linie = L.polyline([z.p0_wgs84, z.p1_wgs84] as L.LatLngExpression[], {
        color: exact ? CULOARE_ZONA_EXACTA : manual ? CULOARE_ZONA_MANUALA : CULOARE_STRADA,
        weight: exact ? 10 : 9, opacity: exact ? 0.95 : 0.7,
        dashArray: exact || manual ? undefined : "12 8", lineCap: "butt",
      }).addTo(g);
      const nume = z.catre || tr("harta.strada");
      const text = exact ? `${tr("harta.zona_exact")} · ${nume}`
        : manual ? `${tr("harta.zona_manual")} · ${nume}`
        : `${tr("harta.zona_din", { sursa: sursaZona(z.sursa) })} · ${nume}`;
      linie.bindTooltip(marchez ? `${text} — ${tr("harta.zona")}` : text, { sticky: true });
      if (marchez) {
        linie.on("contextmenu", (ev) => {
          L.DomEvent.stopPropagation(ev as unknown as Event);
          zoneLucru.current = zoneLucru.current.filter((_, j) => j !== i);
          setStructura((k) => k + 1);
        });
      }
    });
    if (marchez && capat.current) {
      L.marker(capat.current as L.LatLngExpression, {
        icon: CAPAT, interactive: false, keyboard: false, zIndexOffset: 950,
      }).addTo(g);
    }

    if (lat != null && lon != null && mod !== "parcela") {
      // Pinul stă PE adresă, atât. Aici se desena în jurul lui și un halou de
      // incertitudine de 120 m când poziția era doar geocodată — un cerc
      // punctat care spunea „poate fi și pe altă stradă". Întrebarea la care
      // răspunde ecranul e „unde e lotul", iar răspunsul e punctul: haloul nu
      // adăuga o măsurătoare, adăuga o ezitare, peste care oricum se desenează
      // conturul adevărat în pasul următor.
      const mk = L.marker([lat, lon], { icon: PIN, draggable: mod === "pin" }).addTo(g);
      if (comenzi) comenzi.current = null;
      if (mod === "pin") {
        // CIORNA MUTĂRII (v. `onMutare`): o translație și o rotire față de ce
        // e salvat, aplicate PE LOC la tot ce e desenat — conturul, banda
        // retragerii, zona edificabilă, cotele, porțile, bucățile — nu doar la
        // contur. Fiecare strat își ține punctele de la pornire, iar la fiecare
        // cadru primește punctele transformate: rotire în jurul centrului
        // conturului (în metri locali, ca unghiul să fie cel de pe teren), apoi
        // translație. Nimic nu pleacă la server până nu cere părintele.
        const c0 = ciorna.current;
        const centru: [number, number] = contur && contur.length >= 3
          ? centrulWgs(contur) : [lat, lon];
        const mx = 111320 * Math.cos(centru[0] * Math.PI / 180), my = 111132.92;
        const rotit = (p: L.LatLng) => {
          const c = Math.cos(c0.grade * Math.PI / 180), sn = Math.sin(c0.grade * Math.PI / 180);
          const x = (p.lng - centru[1]) * mx, y = (p.lat - centru[0]) * my;
          return L.latLng(centru[0] + (x * sn + y * c) / my, centru[1] + (x * c - y * sn) / mx);
        };
        const transforma = (p: L.LatLng) => {
          const r = rotit(p);
          return L.latLng(r.lat + c0.dLat, r.lng + c0.dLng);
        };
        type Inel = L.LatLng | Inel[];
        const transformaInel = (x: Inel): Inel =>
          Array.isArray(x) ? x.map(transformaInel) : transforma(x);
        const straturi: { strat: L.Polyline | L.Marker; initial: Inel }[] = [];
        g.eachLayer((strat) => {
          if (strat instanceof L.Polyline) {
            straturi.push({ strat, initial: strat.getLatLngs() as Inel });
          } else if (strat instanceof L.Marker && strat !== mk) {
            straturi.push({ strat, initial: strat.getLatLng() });
          }
        });
        const pin0 = mk.getLatLng();
        // Mânerul de rotire, în DREAPTA conturului, la înălțimea centrului:
        // se trage în cerc în jurul centrului, ca bila de rotire a unei piese
        // de pe masa modulului.
        const rotibilAici = !!rotibil && !!contur && contur.length >= 3;
        const lngMax = rotibilAici ? Math.max(...contur!.map((p) => p[1])) : centru[1];
        const maner0 = L.latLng(centru[0], lngMax + (lngMax - centru[1]) * 0.2 + 0.00012);
        const maner = rotibilAici
          ? L.marker(maner0, { icon: ROTIRE, draggable: true, zIndexOffset: 980 }).addTo(g)
          : null;
        maner?.bindTooltip(tr("harta.roteste"), { permanent: true, direction: "right",
                                                   offset: [12, 0] });
        const aplica = (fara?: L.Layer) => {
          for (const { strat, initial } of straturi) {
            if (strat === fara) continue;
            if (strat instanceof L.Marker) strat.setLatLng(transforma(initial as L.LatLng));
            else strat.setLatLngs(transformaInel(initial) as L.LatLngExpression[]);
          }
          if (fara !== mk) mk.setLatLng(transforma(pin0));
          if (maner && fara !== maner) maner.setLatLng(transforma(maner0));
          maner?.setTooltipContent(c0.grade
            ? `${tr("harta.roteste")} ${c0.grade >= 0 ? "+" : ""}${c0.grade.toFixed(1)}°`
            : tr("harta.roteste"));
        };
        // Ciorna de dinaintea reconstrucţiei, pusă la loc pe desenul nou.
        if (c0.dLat || c0.dLng || c0.grade) aplica();
        // Translația care duce pinul (rotit) exact unde e mâna.
        const duPinul = (la: L.LatLng, fara?: L.Layer) => {
          const r = rotit(pin0);
          c0.dLat = la.lat - r.lat;
          c0.dLng = la.lng - r.lng;
          aplica(fara);
        };
        mk.on("drag", () => duPinul(mk.getLatLng(), mk));
        mk.bindTooltip(tr(contur?.length ? "harta.trage_terenul" : "harta.trage_ma"),
                       { permanent: true, direction: "top", offset: [0, -34] });

        // ȘI DE CONTUR SE POATE TRAGE, nu doar de pin: pe un lot de sute de
        // metri pinul e un punct mic în mijloc, iar omul vrea să apuce forma.
        const pol = poligonul.current;
        if (pol) {
          let start: L.LatLng | null = null;
          let pinLaStart: L.LatLng | null = null;
          const laMiscare = (ev: L.LeafletMouseEvent) => {
            if (!start || !pinLaStart) return;
            duPinul(L.latLng(pinLaStart.lat + ev.latlng.lat - start.lat,
                             pinLaStart.lng + ev.latlng.lng - start.lng));
          };
          const laEliberare = () => {
            m.off("mousemove", laMiscare);
            m.off("mouseup", laEliberare);
            m.dragging.enable();
            start = null;
          };
          pol.on("mousedown", (ev: L.LeafletMouseEvent) => {
            L.DomEvent.stopPropagation(ev as unknown as Event);
            m.dragging.disable();
            start = ev.latlng;
            pinLaStart = mk.getLatLng();
            m.on("mousemove", laMiscare);
            m.on("mouseup", laEliberare);
          });
        }

        // ROTIREA: unghiul mânerului față de centrul (translatat) al
        // conturului, minus unghiul lui de pornire.
        if (maner) {
          const unghi = (p: L.LatLng) =>
            Math.atan2((p.lat - centru[0] - c0.dLat) * my,
                       (p.lng - centru[1] - c0.dLng) * mx);
          const u0 = unghi(maner0);
          maner.on("drag", () => {
            c0.grade = Math.round((unghi(maner.getLatLng()) - u0) * 180 / Math.PI * 10) / 10;
            aplica(maner);
          });
          maner.on("dragend", () => aplica());
        }
        if (comenzi) {
          comenzi.current = {
            roteste: (grade) => {
              if (!rotibilAici) return;
              c0.grade = Math.round((c0.grade + grade) * 10) / 10;
              aplica();
            },
            salveaza: () => apel.current.onMutare?.(c0.dLat, c0.dLng, c0.grade),
          };
        }
      }
    }
    // `bucati` și `drumuri` sunt în listă fiindcă desenul le citește: fără
    // ele, o reconsolidare care schimbă doar bucățile (un act urcat în plus,
    // un drum recunoscut) lăsa pe hartă desenul de dinainte până se mișca
    // altceva. Identitatea lor se schimbă doar când se schimbă `loc`, deci nu
    // costă o redesenare pe fiecare randare a părintelui.
  }, [contur, lat, lon, edificabil, mod, structura, viu, deseneaza,
      zone, laturi, bucati, drumuri, retrageriZone, coteContur, coteRetrageri,
      coteSurse, coteCerute, inchide, potriveste,
      potrivestePoligonul, rotibil, comenzi, zoneRegim, limita, zonaEvidentiata,
      interdictieIn]);

  // Fundalul se SCHIMBĂ cât timp se desenează.
  //
  // Termenii Google Maps Platform interzic „No Creating Content From Google
  // Maps Content": conturul unei parcele trasat peste dalele lor devine
  // conținut derivat din conținutul lor — iar la noi conturul ăla intră mai
  // departe în planul de situație și în generarea arhitecturii, adică fix
  // lanțul pe care termenii îl vizează.
  //
  // Google rămâne deci ce are voie să fie: un fundal, pentru orientare. În
  // clipa în care omul intră pe desen, dalele devin OpenStreetMap, care se
  // poate digitiza. Nu e o restricție pe care o punem noi de zel — e diferența
  // dintre „ne uităm pe hartă" și „măsurăm de pe hartă".
  useEffect(() => {
    const m = harta.current, strat = dale.current;
    if (!m || !strat || !cfg) return;
    // Și marcarea zonelor de acces e o măsurătoare de pe hartă: capetele lor
    // intră în planul de situație exact ca vârfurile conturului.
    const seDeseneaza = mod === "parcela" || mod === "zone";
    const treceLaOsm = !!cfg.doar_fundal && seDeseneaza;
    strat.setUrl(treceLaOsm ? OSM_DALE : cfg.tile_url);
    // Atribuirea trebuie să spună mereu al cui e fundalul de SUB ochi, nu al
    // cui era acum o clipă.
    const atrib = m.attributionControl;
    atrib.removeAttribution(cfg.attribution);
    atrib.removeAttribution(OSM_ATRIBUIRE);
    atrib.addAttribution(treceLaOsm ? OSM_ATRIBUIRE : cfg.attribution);
  }, [cfg, mod, viu]);

  // Încadrarea, separată de desen.
  //
  // Stătea în efectul de mai sus și se rula la FIECARE colț pus. Efectul
  // depinde de schiță, deci fiecare clic chema `fitBounds` pe conturul SALVAT
  // și arunca zoom-ul și panoramarea omului — exact „când apăs pe un punct se
  // dă zoom out puțin". Cât timp se editează, harta nu se mai mișcă deloc:
  // singurul care o mai încadrează e omul.
  // Ce s-ar încadra e deja pe ecran, şi destul de aproape ca să se lucreze?
  //
  // Nu doar „în cadru": pe toată Bucureştiul parcela e în cadru şi are opt
  // pixeli. De aceea se cere ŞI o mărire din care se poate desena — aceeaşi cu
  // cea pe care o alege `incadreaza`.
  const eInCadru = useCallback(() => {
    const m = harta.current;
    if (!m || m.getContainer().clientWidth < 200) return false;
    const b = m.getBounds();
    return eDejaInCadru(
      [[b.getSouth(), b.getWest()], [b.getNorth(), b.getEast()]],
      m.getZoom(), contur,
      // `false`, nu `aproximativ`: ecranul nu mai deosebește un punct geocodat
      // de unul măsurat (v. pinul din `Localizare.tsx`), deci nici mărirea de
      // la care se consideră „deja pe ecran" nu mai are voie s-o facă.
      // Funcția își păstrează parametrul — e exportată și testată.
      lat != null && lon != null ? [lat, lon] : null, false);
  }, [contur, lat, lon]);

  useEffect(() => {
    const m = harta.current;
    if (!m) return;
    incadreaza.current = () => {
      // Sub lățimea asta containerul nu s-a așezat încă, iar orice încadrare
      // făcută acum e o minciună. Se așteaptă următorul semnal de dimensiune.
      if (m.getContainer().clientWidth < 200) return;
      if (contur && contur.length >= 3) {
        m.fitBounds(L.latLngBounds(contur as L.LatLngExpression[]).pad(0.35),
                    { animate: false });
      } else if (lat != null && lon != null) {
        m.setView([lat, lon], ZOOM_PUNCT_EXACT, { animate: false });
      } else {
        return;
      }
      incadrat.current = true;
    };
    // Cât se editează — contur, porți SAU PIN — harta nu se mai mișcă singură.
    //
    // «Pin» lipsea de aici, și se vedea: apăsai „Mută pinul" și harta sărea pe
    // parcelă, peste panoramarea pe care tocmai ți-o alesesei. Mutarea pinului
    // e tot o editare — chiar aia în care omul se uită la ALTCEVA decât la
    // parcelă, fiindcă vrea să o ducă acolo.
    if (mod === "parcela" || mod === "zone" || mod === "pin") return;
    // ...și nici după, dacă nu e nimic de adus în cadru.
    //
    // A doua smucitură venea la ELIBERAREA pinului: `lat`/`lon` (şi de la
    // 21.09.2026 şi conturul, care vine după pin) se schimbă, modul e din nou
    // „vezi", efectul se reia şi încadrează. Adică exact peste locul în care
    // omul tocmai pusese pinul — un loc care, prin construcţie, e pe ecran.
    //
    // Acelaşi criteriu ca la intrarea în desen (v. `eAcasa` mai jos): dacă ce
    // s-ar încadra e deja în cadru şi destul de aproape ca să se lucreze pe el,
    // încadrarea nu adaugă nimic şi e doar o smucitură.
    if (eInCadru()) return;
    incadreaza.current();
  }, [contur, lat, lon, mod, viu, eInCadru]);

  // Intrarea în editare pleacă de la conturul CARE EXISTĂ DEJA.
  //
  // Înainte, schița pornea goală: ca să muți un colț trebuia să redesenezi din
  // patru clicuri un dreptunghi care era deja acolo. Ce s-a cerut e „să îl
  // modifici", iar asta înseamnă să-l găsești pe hartă, nu să-l reintroduci.
  // Doar un lot care n-are încă niciun contur se desenează de la zero.
  const modAnterior = useRef(mod);
  useEffect(() => {
    const intra = mod === "parcela" && modAnterior.current !== "parcela";
    modAnterior.current = mod;
    if (mod !== "parcela") {
      schita.current = [];
      setDeseneaza(false);
      return;
    }
    // Doar la INTRARE. Altfel, salvarea conturului (care schimbă `contur`) ar
    // rescrie peste schița la care omul încă lucrează.
    if (!intra) return;
    const plecare = contur && contur.length >= 3 ? contur : [];
    schita.current = plecare.map((p) => [p[0], p[1]] as [number, number]);
    setDeseneaza(plecare.length === 0);
    ultimulAdaugat.current = null;
    setStructura((k) => k + 1);
    // O singură dată, aici — și doar dacă încadrarea chiar ADUCE ceva pe ecran.
    //
    // Ce ar aduce: conturul de ajustat, sau, la desen de la zero, punctul
    // proiectului. Dacă e deja în cadru, încadrarea nu adaugă nimic și e doar o
    // smucitură peste zoomul pe care omul tocmai și l-a ales.
    //
    // Ramura fără contur cerea grijă separată. Acolo nu există niciun mâner de
    // arătat, iar încadrarea cădea pe `setView(pin, 16)`: cine se apropiase la
    // zoom 19 ca să deseneze exact era trimis înapoi la pinul geocodat, de unde
    // un lot de 20 m are opt pixeli. Adică fix „se dă zoom out", mutat de pe
    // clic pe apăsarea butonului. Se cere deci ȘI punctul în cadru, ȘI o mărire
    // din care chiar se poate desena — altfel intrarea în desen de pe harta
    // României ar rămâne la zoom 7.
    const m = harta.current;
    const eAcasa = () => {
      if (!m) return false;
      if (plecare.length >= 3) {
        return m.getBounds().contains(
          L.latLngBounds(plecare as L.LatLngExpression[]));
      }
      if (lat == null || lon == null) return false;
      return m.getBounds().contains(L.latLng(lat, lon))
          && m.getZoom() >= ZOOM_PUNCT_EXACT;
    };
    if (!eAcasa()) incadreaza.current?.();
    // `intra` face din orice rulare care nu e o intrare un nimic, deci
    // dependențele în plus nu costă decât o comparație.
  }, [mod, contur, lat, lon]);

  // Intrarea în marcarea zonelor pleacă de la cele existente — și de la cele
  // din acte: omul le poate scoate sau restrânge, nu doar adăuga.
  useEffect(() => {
    capat.current = null;
    setRefuz("");
    if (mod !== "zone") {
      zoneLucru.current = [];
      return;
    }
    zoneLucru.current = (zone || []).map((z) => ({ ...z }));
    setStructura((k) => k + 1);
    // Doar la schimbarea modului: `zone` în dependențe ar rescrie peste
    // zonele la care omul încă lucrează, la fiecare răspuns de la server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mod]);

  // Două clicuri pe CONTUR delimitează o zonă de acces; un clic pe restul
  // hărții nu face nimic.
  useEffect(() => {
    const m = harta.current;
    if (!m || mod !== "zone") return undefined;
    const pune = (e: L.LeafletMouseEvent) => {
      const ring = (contur || []) as [number, number][];
      const pe = lipesteLaContur(ring, [e.latlng.lat, e.latlng.lng]);
      if (!pe) return;
      // Departe de contur nu e un capăt pus strâmb, e un clic care voia
      // altceva — dar SE SPUNE. Refuzat în liniște, omul apăsa pe axul
      // bulevardului (la 15–20 m de limita lotului, adică peste toleranța
      // de 34 px la mărirea obișnuită) și nu înțelegea de ce al doilea capăt
      // nu se pune (Oana, 29.09.2026, pe Bd. Timișoara 82).
      const a = m.latLngToContainerPoint(pe as L.LatLngExpression);
      const b = m.latLngToContainerPoint(e.latlng);
      if (a.distanceTo(b) > LIPIRE_PX) {
        setRefuz(tr("harta.zona_departe"));
        return;
      }
      if (!capat.current) {
        capat.current = pe;
        setRefuz("");
        setStructura((k) => k + 1);
        return;
      }
      // Al doilea capăt: pe ACEEAȘI latură LOGICĂ — una dreaptă, chiar dacă
      // planșa o are din mai multe bucăți aproape coliniare (v.
      // `laturaLogica`). Peste un colț adevărat segmentul n-ar mai fi pe
      // contur, și se spune, nu se refuză în tăcere.
      const i0 = laturaDeSub(ring, capat.current), i1 = laturaDeSub(ring, pe);
      const logica = i0 === null || i1 === null ? null : laturaLogica(ring, i0, i1);
      if (!logica) {
        setRefuz(tr("harta.zona_alta_latura"));
        return;
      }
      if (lungime(capat.current, pe) < LATIME_MIN_ZONA_M) {
        setRefuz(tr("harta.zona_scurta", { metri: numar(LATIME_MIN_ZONA_M) }));
        return;
      }
      zoneLucru.current = [...zoneLucru.current, {
        p0_wgs84: capat.current, p1_wgs84: pe, sursa: "manual", exact: false,
        catre: logica.map((i) => laturi?.[i]?.catre).find((c) => !!c) ?? null,
      }];
      capat.current = null;
      setRefuz("");
      setStructura((k) => k + 1);
    };
    m.on("click", pune);
    return () => { m.off("click", pune); };
  }, [mod, contur, viu, laturi]);

  // Clic pe hartă adaugă un colț — dar NUMAI cât timp desenăm unul nou.
  useEffect(() => {
    const m = harta.current;
    if (!m || mod !== "parcela") return undefined;
    const pune = (e: L.LeafletMouseEvent) => {
      if (!deseneaza) return;
      const acum = Date.now();
      const ultim = schita.current[schita.current.length - 1];
      if (ultim) {
        // Al doilea clic al unui dublu-clic ajunge tot aici: Leaflet trimite
        // click, click, dblclick, fără să le filtreze. Fără gardă, închiderea
        // prin dublu-clic lăsa două vârfuri exact peste ultimul.
        //
        // Se cere ȘI aproape în același loc, ȘI aproape în aceeași clipă.
        // Numai distanța nu ajunge: la zoomul maxim 12 px înseamnă vreo 2,5 m,
        // iar un colț pus dinadins la doi metri de precedentul — o retragere,
        // un prag — e un colț cât se poate de legitim, și ar fi fost refuzat
        // în tăcere.
        const a = m.latLngToContainerPoint(ultim as L.LatLngExpression);
        const b = m.latLngToContainerPoint(e.latlng);
        if (a.distanceTo(b) < 12 && acum - ultimulClic.current < 400) {
          ultimulClic.current = acum;
          return;
        }
      }
      ultimulClic.current = acum;
      schita.current = [...schita.current, [e.latlng.lat, e.latlng.lng]];
      ultimulAdaugat.current = schita.current.length - 1;
      setStructura((k) => k + 1);
    };
    m.on("click", pune);
    // Dublu-clicul închide conturul DOAR cât timp se desenează unul nou. În
    // ajustare, un dublu-clic rătăcit salva și ieșea din editare — iar zoomul
    // pe dublu-clic n-avea de ce să stea stins acolo.
    if (!deseneaza) return () => { m.off("click", pune); };
    m.on("dblclick", inchide);
    m.doubleClickZoom.disable();
    return () => {
      m.off("click", pune);
      m.off("dblclick", inchide);
      m.doubleClickZoom.enable();
    };
  }, [mod, deseneaza, inchide, viu]);

  return (
    <div className="harta">
      <div ref={gazda} style={{ width: "100%", height: "100%" }} />
      {ocupat && (
        <div className="harta-ocupat" role="status" aria-busy="true">
          <div className="harta-ocupat-pastila"><span className="pulsa" />{ocupat}</div>
        </div>
      )}
      {eroare && (
        <div className="harta-gol">
          <b>{t("harta.esec")}</b>
          <span>{eroare}</span>
        </div>
      )}
      {/* Sus-DREAPTA, nu sus-stânga: colțul din stânga e al cartonașului de
          adresă, iar cele două se suprapuneau exact. */}
      {mod === "parcela" && (() => {
        const nr = schita.current.length;
        const a = arie(schita.current);
        const taiat = seTaie(schita.current);
        const dif = suprafataAsteptata && a
          ? (a - suprafataAsteptata) / suprafataAsteptata : null;
        const potrivit = dif !== null && Math.abs(dif) <= 0.02;
        return (
          <div className="float card" style={{ right: 14, top: 14, padding: "10px 13px",
                                               zIndex: 1000, maxWidth: 300,
                                               display: "flex", flexDirection: "column",
                                               gap: 5 }}>
            <span className="lbl">
              {t(deseneaza ? "harta.desenezi" : "harta.ajustezi")}
            </span>
            {/* Instrucțiunea se schimbă cu ce se poate face ACUM. Un text care
                le spune pe toate deodată e textul pe care nimeni nu-l citește,
                iar „dublu-clic ca să închizi" scris peste un contur gata
                desenat era exact sfatul greșit. */}
            <div style={{ fontSize: 12, lineHeight: 1.45 }}>
              {deseneaza
                ? <>{t("harta.instructiuni_desen_1")}
                    <b>{t("harta.instructiuni_desen_2")}</b>
                    {t("harta.instructiuni_desen_3")}</>
                : <>{t("harta.instructiuni_ajustare_1")}
                    <b>{t("harta.instructiuni_ajustare_2")}</b>
                    {t("harta.instructiuni_ajustare_3")}
                    <b>{t("harta.instructiuni_ajustare_4")}</b>
                    {t("harta.instructiuni_ajustare_5")}</>}
              {nr > 0 && <b>{tn("harta.colturi#n", nr)}</b>}
            </div>
            {/* Cifra care spune dacă e lotul DIN ACTE sau altceva. Fără ea,
                orice patrulater desenat pe hartă arată la fel de convingător. */}
            {a > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span className="mono">
                  {t("harta.arie", { suprafata: numar(Math.round(a)) })}
                </span>
                {suprafataAsteptata ? (
                  potrivit
                    ? <span className="chip c-ok">{t("harta.se_potriveste")}</span>
                    : <span className="chip c-amb">
                        {t("harta.actele_spun",
                           { suprafata: numar(suprafataAsteptata) })}
                        {dif !== null && t("harta.diferenta", {
                          semn: dif > 0 ? "+" : "",
                          procent: Math.round(dif * 100),
                        })}
                      </span>
                ) : null}
              </div>
            )}
            {/* Schimbarea fundalului nu se face pe furiș: omul vede harta
                altfel decât acum o clipă și merită să știe de ce. */}
            {cfg?.doar_fundal && (
              <span style={{ color: "var(--mut)", fontSize: 11 }}>
                {t("harta.fundal_desen")}
              </span>
            )}
            {taiat && (
              <span className="chip c-bad">{t("harta.se_taie")}</span>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {deseneaza && nr > 0 && (
                <button className="btn" style={{ height: 26, fontSize: 11 }}
                        onClick={() => {
                          const i = ultimulAdaugat.current
                            ?? schita.current.length - 1;
                          schita.current = schita.current.filter((_, j) => j !== i);
                          ultimulAdaugat.current = null;
                          setStructura((k) => k + 1);
                        }}>
                  {t("harta.sterge_coltul")}
                </button>
              )}
              {/* Drumul înapoi la desenul de la zero. Există doar când chiar
                  s-a plecat de la un contur — altfel butonul n-ar face nimic. */}
              {!deseneaza && (
                <button className="btn" style={{ height: 26, fontSize: 11 }}
                        onClick={() => {
                          schita.current = [];
                          ultimulAdaugat.current = null;
                          setDeseneaza(true);
                          setStructura((k) => k + 1);
                        }}>
                  {t("harta.de_la_zero")}
                </button>
              )}
              <button className="btn btnp" style={{ height: 26, fontSize: 11 }}
                      disabled={nr < 3 || taiat}
                      title={taiat ? t("harta.se_taie_scurt") : ""}
                      onClick={() => onParcela?.(schita.current)}>
                {t("harta.gata")}
              </button>
            </div>
          </div>
        );
      })()}
      {mod === "zone" && (() => {
        const lista = zoneLucru.current;
        const schimbat = JSON.stringify(lista) !== JSON.stringify(zone || []);
        const culoare = (z: ZonaAcces) => z.exact ? CULOARE_ZONA_EXACTA
          : z.sursa === "manual" ? CULOARE_ZONA_MANUALA : CULOARE_STRADA;
        const fel = (z: ZonaAcces) => z.exact ? t("harta.zona_exact")
          : z.sursa === "manual" ? t("harta.zona_manual")
          : t("harta.zona_din", { sursa: sursaZona(z.sursa) });
        return (
          <div className="float card" style={{ right: 14, top: 14, padding: "10px 13px",
                                               zIndex: 1000, maxWidth: 300,
                                               display: "flex", flexDirection: "column",
                                               gap: 5 }}>
            <span className="lbl">{t("harta.marchezi_zone")}</span>
            <div style={{ fontSize: 12, lineHeight: 1.45 }}>
              <b>{t("harta.instructiuni_zone_1")}</b>
              {t("harta.instructiuni_zone_2")}
            </div>
            {capat.current && !refuz && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span className="chip c-amb">{t("harta.capat_pus")}</span>
                {/* Primul capăt pus strâmb se lasă, nu se duce până la o zonă
                    întreagă ca să fie scoasă pe urmă. */}
                <button className="btn" style={{ height: 22, fontSize: 11 }}
                        onClick={() => { capat.current = null; setStructura((k) => k + 1); }}>
                  {t("harta.renunta_capat")}
                </button>
              </div>
            )}
            {refuz && <span className="chip c-amb">{refuz}</span>}
            {lista.length === 0 ? (
              <span className="chip c-amb">{t("harta.fara_zona")}</span>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {lista.map((z, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center",
                                        gap: 6, fontSize: 12 }}>
                    <span className="mono">{i + 1}</span>
                    <span style={{ width: 14, height: 4, flex: "none",
                                   background: culoare(z) }} />
                    <span>{z.catre || t("harta.strada")}</span>
                    <span style={{ color: "var(--mut)" }}>{fel(z)}</span>
                    {/* Scoaterea și din listă, nu doar prin clic-dreapta pe
                        linie: o zonă pusă greșit se vede aici cu numărul ei,
                        iar clicul-dreapta pe o linie groasă de pe hartă nu e
                        ceva ce omul încearcă de la sine. */}
                    <button className="btn" title={t("harta.scoate_zona")}
                            aria-label={t("harta.scoate_zona")}
                            style={{ height: 20, minWidth: 20, padding: "0 5px",
                                     fontSize: 12, lineHeight: 1, marginLeft: "auto" }}
                            onClick={() => {
                              zoneLucru.current = zoneLucru.current.filter((_, j) => j !== i);
                              setStructura((k) => k + 1);
                            }}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {cfg?.doar_fundal && (
              <span style={{ color: "var(--mut)", fontSize: 11 }}>
                {t("harta.fundal_zone")}
              </span>
            )}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {/* Se salvează orice SCHIMBARE, inclusiv lista goală: altfel
                  singura zonă, pusă greșit, nu se putea scoate — butonul
                  stătea stins exact după ce omul o ștersese. */}
              <button className="btn btnp" style={{ height: 26, fontSize: 11 }}
                      disabled={!schimbat}
                      onClick={() => apel.current.onZone?.(zoneLucru.current)}>
                {t("harta.gata")}
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
